import { CommonModule } from '@angular/common';
import { SsSelectClearDirective } from '../shared/ss-select-clear.directive';
import { RelationHopsComponent } from './relation-hops.component';
import { copyRelationHops, RelationHop, RelationHopsConfig } from './relation-hops.types';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Component, ElementRef, HostListener, OnInit, ViewChild, ViewEncapsulation, forwardRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../shared/ss-select-tooltip.directive';
import type * as echarts from 'echarts';

import { DrillDownComponent } from '../../chart-components/drill-down/drill-down.component';
import { NestedTableComponent } from '../components/nested-table/nested-table.component';
import { UnifiedTableMatrixChartComponent } from '../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.component';
import { DvmFileUploadComponent } from '../../chart-components/dvm-file-upload/dvm-file-upload.component';
import { DvmPopupComponent } from '../../chart-components/dvm-popup/dvm-popup.component';
import type { TableMatrixSaveEvent, TableMatrixPayloadTemplate } from '../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.types';
import { applyDrillDownSaveToWidgetData } from '../../chart-components/unified-table-matrix-chart/drilldown-save.utils';
import { HorizontalBarChartComponent } from '../../chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { UnifiedBarColumnChartComponent } from '../../chart-components/unified-bar-column-chart/unified-bar-column-chart.component';
import { UnifiedScatterBubbleChartComponent } from '../../chart-components/unified-scatter-bubble-chart/unified-scatter-bubble-chart.component';
import { UnifiedLineAreaChartComponent } from '../../chart-components/unified-line-area-chart/unified-line-area-chart.component';
import { UnifiedPieCompositionChartComponent } from '../../chart-components/unified-pie-composition-chart/unified-pie-composition-chart.component';
import { WaterfallChartComponent } from '../../chart-components/waterfall-chart/waterfall-chart.component';
import { MatSliderComponent } from '../../global-components/mat-slider/mat-slider.component';
import { InputBoxComponent } from '../../global-components/input-box/input-box.component';
import { GlobalDropdownComponent } from '../../global-components/global-dropdown/global-dropdown.component';
import { NumberStatsComponent } from '../../global-components/number-stats/number-stats.component';
import { CapitalCasePipe } from '../../pipes/capital-case.pipe';
import { SelfServiceUiService } from '../shared/self-service-ui.service';
import { SelfServiceConfigService } from '../shared/self-service-config.service';
import { SelfServiceDatasetService } from '../shared/self-service-dataset.service';
import { resolveSelfServiceApiUrl, buildSelfServiceApiUrl } from '../shared/self-service-api.utils';
import { recomputeEntryHasData, resolveRecomputeChartType } from '../shared/self-service-recompute.utils';
import { stripWidgetData } from '../shared/self-service-json.utils';
import { SelfServiceDashboardService } from '../shared/self-service-dashboard.service';
import { layoutHasNavTargets, navTargetComponentOptionsFromLayout } from '../shared/nav-target.utils';
import { SelfServiceChartPersistenceService } from '../shared/self-service-chart-persistence.service';
import { ConfirmationDialogService } from '../../shared-services/confirmation-dialog.service';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { BuilderLayoutConfig, CapabilityRegistryEntry, ChartCategory, FormatControl, FormatControlGroup, FormulaBuilderConfig, LabelValueOption, LayoutStepsDefinition } from '../shared/self-service-config.types';
import { SavedSelfServiceDashboard, ComputedColumn } from '../shared/self-service-types';
import { SelfServiceFormulaService } from '../shared/self-service-formula.service';
import { markDashboardJustPublished, readDynamicDashboardCache, readJson, writeDynamicDashboardCache } from '../shared/self-service-storage.utils';
import { DashboardTile, InputApiCallConfig, TargetFormulaReferenceEntry, WaterfallBar } from './dvm-layout-builder.types';

import { DvmBuilderHost, DvmBuilderStep } from './dvm-builder-host';
import { KpiStepComponent } from '../components/kpi-step/kpi-step.component';
import { FileUploadStepComponent } from '../components/file-upload-step/file-upload-step.component';
import { InputControlStepComponent } from '../components/input-control-step/input-control-step.component';
import { DropdownStepComponent } from '../components/dropdown-step/dropdown-step.component';
import { PopupStepComponent } from '../components/popup-step/popup-step.component';
import { refreshPopupTilesForDataset } from '../components/popup-step/popup-widget.utils';
import { HorizontalBarStepComponent } from '../components/horizontal-bar-step/horizontal-bar-step.component';
import { WaterfallStepComponent } from '../components/waterfall-step/waterfall-step.component';
import { ScatterBubbleStepComponent } from '../components/scatter-bubble-step/scatter-bubble-step.component';
import { PieCompositionStepComponent } from '../components/pie-composition-step/pie-composition-step.component';
import { BarColumnStepComponent } from '../components/bar-column-step/bar-column-step.component';
import { applyComputedColumns } from '../components/bar-column-step/bar-computed-columns.utils';
import { LineAreaStepComponent } from '../components/line-area-step/line-area-step.component';
import { DrillDownStepComponent } from '../components/drill-down-step/drill-down-step.component';
import { TableMatrixStepComponent } from '../components/table-matrix-step/table-matrix-step.component';
import { DASH_COLS, DASH_GAP, DASH_ROW_PX, TileRestoreContext, applyPublishedLayoutToTiles, buildDashboardId, buildDashboardLayoutJson, clampNumber, cloneRecord, createDashboardStableId, dashboardSlug, extractHttpErrorDetail, hasRenderableContent, isFormattableTile, normalizeDashboardName, tileFromWidget, tileTakesHeaderFormat } from './dvm-dashboard-layout-json.utils';
import { buildPublishEnvelope } from '../shared/dashboard-layouts-envelope.utils';
import { NestedTableStepComponent } from '../components/nested-table-step/nested-table-step.component';
import {
  applyCrossFilterToClientTiles, applyDropdownFiltersToClientTiles, canConfigureCrossFilter, effectiveListenerTileIds, effectiveTileFilterKey,
  isChartTile, isCrossFilterTile, isRecomputeCrossFilterTile, reactableTilesFor, tileClientRows, tileDatasets,
} from './dvm-tile-crossfilter.utils';
import { API_CALL_MODE_OPTIONS, defaultApiCallConfig, fileToBase64, findRecomputeTargetTile, otherActiveInputValues, sanitizeApiCallConfig, sideEffectInputsFor } from './dvm-input-api-call.utils';
import { DvmInputComputeService } from './dvm-input-compute.service';
import {
  buildGroupTile, canMoveGroupMember, findGroupMemberLocation, findTileAnywhere, flattenTiles, groupCardStyleMap, groupTiles, moveGroupMember,
  replaceTileInPlace, ungroupTiles,
} from './dvm-tile-groups.utils';
import {
  applicableFormatControls, capabilitiesForType, categoryOfType, chartCategoriesOf, chartExposesSection, chartSupports, controlVisibleForSubType,
  fieldEnabled, fieldLabel, fontWeightOptionsOf, formatControlVisible, formatControlsOf, formatGroupOrderOf, formulaBuilderConfigOf,
  formulaReferencesEnabledFor, groupFormatControls, layoutSectionHint, layoutSectionTitle, layoutStepsFor, layoutTypesOf, measureAggregationsOf,
  numberFormatsOf, resolvePaletteColors, typesForCategory, DVM_LAYOUT_BUILDER_DEFAULT_LAYOUT, componentIdPrefixFor,
} from './dvm-builder-config.utils';
import { TileInteractionsModalComponent, TileInteractionsSave } from './tile-interactions-modal.component';
import { PayloadTemplateModalComponent, PayloadTemplateModalRequest, hasPayloadTemplate, payloadTemplatesOn } from './payload-template-modal.component';
import { applyStepSignsToWaterfallBars, buildWaterfallPreviewOption, buildWaterfallTileData, waterfallTileFromResponse } from '../components/waterfall-step/waterfall-tile.utils';

export * from './dvm-layout-builder.types';
export { DvmBuilderHost } from './dvm-builder-host';
export type { DvmBuilderStep } from './dvm-builder-host';

type BuilderPane = 'preview' | 'steps' | 'data';
const PANE_DRAG_TYPE = 'application/x-dvm-pane';
const FIELD_DRAG_TYPE = 'application/x-dvm-field';
const TILE_DRAG_TYPE = 'application/x-dvm-tile';


@Component({
  selector: 'app-dvm-layout-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    NgSelectModule,
    SsSelectTooltipDirective,
    DrillDownComponent,
    NestedTableComponent,
    UnifiedTableMatrixChartComponent,
    DvmFileUploadComponent,
    DvmPopupComponent,
    HorizontalBarChartComponent,
    UnifiedBarColumnChartComponent,
    UnifiedScatterBubbleChartComponent,
    UnifiedLineAreaChartComponent,
    UnifiedPieCompositionChartComponent,
    WaterfallChartComponent,
    MatSliderComponent,
    InputBoxComponent,
    GlobalDropdownComponent,
    NumberStatsComponent,
    CapitalCasePipe,
    RelationHopsComponent,
    SsSelectClearDirective,
    KpiStepComponent,
    FileUploadStepComponent,
    InputControlStepComponent,
    DropdownStepComponent,
    PopupStepComponent,
    HorizontalBarStepComponent,
    WaterfallStepComponent,
    ScatterBubbleStepComponent,
    PieCompositionStepComponent,
    BarColumnStepComponent,
    LineAreaStepComponent,
    DrillDownStepComponent,
    TableMatrixStepComponent,
    TileInteractionsModalComponent,
    PayloadTemplateModalComponent, NestedTableStepComponent,],
  templateUrl: './dvm-layout-builder.component.html',
  styleUrl: './dvm-layout-builder.component.scss',
  // The step components are plain children that share THIS sheet: every rule
  // in it is nested under .dvm-layout-builder-root (the host class below), so
  // encapsulation is switched off instead of duplicating 2.6k lines per step.
  encapsulation: ViewEncapsulation.None,
  host: { class: 'dvm-layout-builder-root' },
  // Steps reach the shell only through the DvmBuilderHost contract.
  providers: [{ provide: DvmBuilderHost, useExisting: forwardRef(() => DvmLayoutBuilderComponent) }],
})
export class DvmLayoutBuilderComponent implements OnInit, DvmBuilderHost {
  relationHops: RelationHop[] = [];

  get activeRelationHops(): RelationHopsConfig | null {
    const config = this.layout.relationHops;
    return config?.enabled && config.dropdowns?.length &&
      config.categoryIds.includes(this.categoryOfType(this.selectedLayoutType)) ? config : null;
  }

  private readonly http = inject(HttpClient);
  private readonly ui = inject(SelfServiceUiService);
  private readonly configService = inject(SelfServiceConfigService);
  private readonly datasetService = inject(SelfServiceDatasetService);
  private readonly formulaService = inject(SelfServiceFormulaService);
  private readonly dashboardService = inject(SelfServiceDashboardService);
  private readonly confirmationDialog = inject(ConfirmationDialogService);
  private static readonly SAVED_DASHBOARDS_STORAGE_KEY = 'dvm.selfService.savedDashboards';

  private readonly persistence = inject(SelfServiceChartPersistenceService);
  private readonly inputCompute = inject(DvmInputComputeService);
  private readonly keycloak = inject(KeycloakService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  static readonly STEP_FIELD_IDS: ReadonlySet<string> = new Set<string>(['dvmLayoutWorkbench']);

  layout: BuilderLayoutConfig = DVM_LAYOUT_BUILDER_DEFAULT_LAYOUT;

  // ── Step components ─────────────────────────────────────────────────────
  // Each widget family's build form lives in self-service-visualization/
  // components/<family>-step. They are ALWAYS instantiated (each gates its own
  // template on `active`), so these static queries resolve before ngOnInit and
  // the hubs below (Generate / Edit / New) can call them synchronously.
  @ViewChild(KpiStepComponent, { static: true }) private kpiStep!: KpiStepComponent;
  @ViewChild(FileUploadStepComponent, { static: true }) private fileUploadStep!: FileUploadStepComponent;
  @ViewChild(InputControlStepComponent, { static: true }) private inputControlStep!: InputControlStepComponent;
  @ViewChild(DropdownStepComponent, { static: true }) private dropdownStep!: DropdownStepComponent;
  @ViewChild(PopupStepComponent, { static: true }) private popupStep!: PopupStepComponent;
  @ViewChild(HorizontalBarStepComponent, { static: true }) private horizontalBarStep!: HorizontalBarStepComponent;
  @ViewChild(WaterfallStepComponent, { static: true }) private waterfallStep!: WaterfallStepComponent;
  @ViewChild(ScatterBubbleStepComponent, { static: true }) private scatterBubbleStep!: ScatterBubbleStepComponent;
  @ViewChild(PieCompositionStepComponent, { static: true }) private pieCompositionStep!: PieCompositionStepComponent;
  @ViewChild(BarColumnStepComponent, { static: true }) private barColumnStep!: BarColumnStepComponent;
  @ViewChild(LineAreaStepComponent, { static: true }) private lineAreaStep!: LineAreaStepComponent;
  @ViewChild(DrillDownStepComponent, { static: true }) drillDownStep!: DrillDownStepComponent;
  @ViewChild(TableMatrixStepComponent, { static: true }) private tableMatrixStep!: TableMatrixStepComponent;
  @ViewChild(NestedTableStepComponent, { static: true }) private nestedTableStep!: NestedTableStepComponent;

  /** Every step, for whole-form operations (reset). Steps are absent when the component is
   *  instantiated without its view (unit tests build it with `new`), so undefined entries are skipped. */
  private get allSteps(): DvmBuilderStep[] {
    const steps: Array<DvmBuilderStep | undefined> = [this.kpiStep, this.fileUploadStep, this.inputControlStep, this.dropdownStep, this.popupStep, this.horizontalBarStep, this.waterfallStep, this.scatterBubbleStep, this.pieCompositionStep, this.barColumnStep, this.lineAreaStep, this.drillDownStep, this.tableMatrixStep, this.nestedTableStep];
    return steps.filter((step): step is DvmBuilderStep => !!step);
  }

  /** The step that builds `layoutType`, or null for an unknown type (or when the view is not rendered). */
  private stepFor(layoutType: string): DvmBuilderStep | null {
    switch (layoutType) {
      case 'KPI': return this.kpiStep ?? null;
      case 'FILE_UPLOAD': return this.fileUploadStep ?? null;
      case 'INPUT_BOX':
      case 'SLIDER': return this.inputControlStep ?? null;
      case 'DROPDOWN': return this.dropdownStep ?? null;
      case 'POPUP': return this.popupStep ?? null;
      case 'HORIZONTAL_BAR_CHART': return this.horizontalBarStep ?? null;
      case 'WATERFALL': return this.waterfallStep ?? null;
      case 'SCATTER_BUBBLE': return this.scatterBubbleStep ?? null;
      case 'PIE_COMPOSITION': return this.pieCompositionStep ?? null;
      case 'BAR': return this.barColumnStep ?? null;
      case 'LINE_AREA': return this.lineAreaStep ?? null;
      case 'DRILL_DOWN_TABLE': return this.drillDownStep ?? null;
      case 'TABLE_MATRIX': return this.tableMatrixStep ?? null;
      case 'NESTED_TABLE': return this.nestedTableStep ?? null;
      default: return null;
    }
  }

  /** Pane-header "Add step" (waterfall only). */
  addWaterfallStep(): void {
    this.waterfallStep.addStep();
  }

  /** A popup tile's button asked to open another dashboard. */
  onPopupNavigate(slug: string): void {
    if (slug) this.router.navigate(['/dvm-published', slug]);
  }

  /** The step of the selected layout type. */
  get activeStep(): DvmBuilderStep | null {
    return this.stepFor(this.selectedLayoutType);
  }

  get layoutTypes(): LabelValueOption[] {
    return layoutTypesOf(this.layout);
  }

  get chartCategories(): ChartCategory[] {
    return chartCategoriesOf(this.layout);
  }

  get hasChartCatalogue(): boolean {
    return this.chartCategories.length > 0;
  }

  private typesForCategoryCache: LabelValueOption[] = [];
  private typesForCategoryKey = '';
  get typesForSelectedCategory(): LabelValueOption[] {
    const category = this.chartCategories.find((c) => c.id === this.selectedChartCategory);
    if (!category) return this.layoutTypes;
    const key = `${category.id}::${category.types.join('|')}::${this.layoutTypes.length}`;
    if (key !== this.typesForCategoryKey) {
      // memoised: ng-select needs a stable [items] reference
      this.typesForCategoryCache = typesForCategory(this.layout, category);
      this.typesForCategoryKey = key;
    }
    return this.typesForCategoryCache;
  }

  private categoryOfType(type: string): string {
    return categoryOfType(this.layout, type);
  }

  onChartCategoryChange(): void {
    const types = this.typesForSelectedCategory;
    if (types.length && !types.some((t) => t.value === this.selectedLayoutType)) {
      this.selectedLayoutType = types[0].value;
      this.onLayoutTypeChange();
    }
  }


  capabilitiesForType(type: string): CapabilityRegistryEntry {
    return capabilitiesForType(this.layout, type);
  }

  get activeCapabilities(): CapabilityRegistryEntry {
    return this.capabilitiesForType(this.selectedLayoutType);
  }

  chartSupports(capability: string): boolean {
    return chartSupports(this.layout, this.selectedLayoutType, capability);
  }

  chartExposesSection(section: string): boolean {
    return chartExposesSection(this.layout, this.selectedLayoutType, section);
  }

  get isWaterfallLayout(): boolean {
    return this.selectedLayoutType === 'WATERFALL';
  }
  get isDrillDownLayout(): boolean {
    return this.selectedLayoutType === 'DRILL_DOWN_TABLE';
  }
  get isNestedTableLayout(): boolean {
    return this.selectedLayoutType === 'NESTED_TABLE';
  }

  /** The type picker shows a nested table as the Table & Matrix it belongs to. */
  get layoutTypePickerValue(): string {
    return this.isNestedTableLayout ? 'TABLE_MATRIX' : this.selectedLayoutType;
  }

  switchTableType(type: string): void {
    const nested = type === 'nested';
    if (!nested) this.tableMatrixStep.tmType = type;
    this.selectedLayoutType = nested ? 'NESTED_TABLE' : 'TABLE_MATRIX';
    this.onLayoutTypeChange();
    if (!nested) this.tableMatrixStep.onTableMatrixTypeChange();
  }
  get isHorizontalBarLayout(): boolean {
    return this.selectedLayoutType === 'HORIZONTAL_BAR_CHART';
  }
  get isBarLayout(): boolean {
    return this.selectedLayoutType === 'BAR';
  }
  get isScatterBubbleLayout(): boolean {
    return this.selectedLayoutType === 'SCATTER_BUBBLE';
  }
  get isPieCompositionLayout(): boolean {
    return this.selectedLayoutType === 'PIE_COMPOSITION';
  }
  get isLineAreaLayout(): boolean {
    return this.selectedLayoutType === 'LINE_AREA';
  }

  get buildPanelTitle(): string {
    if (this.isDrillDownLayout) return 'Drill-down table';
    if (this.isNestedTableLayout) return 'Nested table';
    if (this.isBarLayout) return 'Bar & column';
    if (this.isScatterBubbleLayout) return 'Scatter & bubble';
    if (this.isHorizontalBarLayout) return 'Horizontal bar';
    if (this.isKpiLayout) return 'KPI card';
    if (this.isInputControlLayout) return this.isSliderLayout ? 'Slider input' : 'Input box';
    return 'Bridge steps';
  }

  get buildPanelCount(): number | null {
    if (this.isDrillDownLayout) return this.drillDownStep.drillDownDimensions.length;
    if (this.isBarLayout) return this.barColumnStep.barMeasures.length;
    if (this.isWaterfallLayout) return this.waterfallStep.steps.length;
    return null;
  }
  get isKpiLayout(): boolean {
    return this.selectedLayoutType === 'KPI';
  }

  get isDropdownLayout(): boolean {
    return this.selectedLayoutType === 'DROPDOWN';
  }

  get isFileUploadLayout(): boolean {
    return this.selectedLayoutType === 'FILE_UPLOAD';
  }

  get isPopupLayout(): boolean {
    return this.selectedLayoutType === 'POPUP';
  }

  get isTableMatrixLayout(): boolean {
    return this.selectedLayoutType === 'TABLE_MATRIX';
  }

  get isChartTargetLayout(): boolean {
    return !this.isInputControlLayout && !this.isDropdownLayout && !this.isFileUploadLayout && !this.isPopupLayout;
  }

  private navDashboardsMemo: { key: string; value: LabelValueOption[] } | null = null;
  get tmNavTargetDashboards(): LabelValueOption[] {
    const options: LabelValueOption[] = this.apiTargetDashboards.map((d) => ({ label: d.label, value: d.value }));
    const seen = new Set(options.map((o) => o.value));
    const saved = this.persistence.consolidateSavedDashboards(
      readJson<SavedSelfServiceDashboard[]>(DvmLayoutBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY, []),
    );
    saved.forEach((dashboard) => {
      const slug = String(dashboard.config?.['dashboardId'] ?? '');
      if (slug && !seen.has(slug)) {
        seen.add(slug);
        options.push({ label: dashboard.name || slug, value: slug });
      }
    });
    if (this.navPinnedTarget && !seen.has(this.navPinnedTarget.value)) {
      options.push(this.navPinnedTarget);
      seen.add(this.navPinnedTarget.value);
    }
    const key = options.map((o) => o.value).join('|');
    if (!this.navDashboardsMemo || this.navDashboardsMemo.key !== key) {
      this.navDashboardsMemo = { key, value: options };
    }
    return this.navDashboardsMemo.value;
  }

  private navPinnedTarget: LabelValueOption | null = null;
  ensureNavTargetInList(slug: string): void {
    const existing = [...this.apiTargetDashboards.map((d) => d.value)];
    if (existing.includes(slug)) { this.navPinnedTarget = null; return; }
    const apiHit = this.apiTargetDashboards.find((d) => d.value === slug);
    const label = apiHit?.label
      || slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    this.navPinnedTarget = { label, value: slug };
    this.navDashboardsMemo = null; // force the getter to include the pin
  }

  private navComponentsMemo: { key: string; value: LabelValueOption[] } | null = null;
  navTargetComponentOptions(slug: string): LabelValueOption[] {
    if (!slug) { this.navComponentsMemo = { key: '', value: [] }; return []; }
    const apiEntry = this.apiTargetDashboards.find((d) => d.value === slug);
    let config: Record<string, unknown> = apiEntry?.layoutJson || {};
    if (!layoutHasNavTargets(config)) {
      const saved = this.persistence.consolidateSavedDashboards(
        readJson<SavedSelfServiceDashboard[]>(DvmLayoutBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY, []),
      );
      const dashboard = saved.find((d) => String(d.config?.['dashboardId'] ?? '') === slug);
      config = (dashboard?.config as Record<string, unknown>) || {};
    }
    const options = navTargetComponentOptionsFromLayout(config);
    const key = slug + '::' + options.map((o) => o.value).join('|');
    if (!this.navComponentsMemo || this.navComponentsMemo.key !== key) {
      this.navComponentsMemo = { key, value: options };
    }
    return this.navComponentsMemo.value;
  }

  formulaReferencesEnabledFor(layoutType: string): boolean {
    return formulaReferencesEnabledFor(this.layout, layoutType);
  }

  targetLayoutTypeForId(id: string): string {
    return this.allTilesFlat().find((tile) => tile.componentId === id)?.layoutType || '';
  }

  isDuplicateReferenceToken(id: string, ref: TargetFormulaReferenceEntry, step?: string): boolean {
    const token = ref.token.trim();
    if (!token) return false;
    return this.targetReferences(id, step).some((other) => other !== ref && other.token.trim() === token);

  }
  get isInputBoxLayout(): boolean {
    return this.selectedLayoutType === 'INPUT_BOX';
  }
  get isSliderLayout(): boolean {
    return this.selectedLayoutType === 'SLIDER';
  }
  get isInputControlLayout(): boolean {
    return this.isInputBoxLayout || this.isSliderLayout;
  }
  private targetChartsCache: LabelValueOption[] = [];
  private targetChartsKey = '';

  get apiCallTargetOptions(): LabelValueOption[] {
    const options = this.targetCharts;
    const key = `${this.targetChartsKey}~${this.selectedTargetCharts.join('|')}`;
    if (key !== this.apiCallTargetOptionsKey) {
      this.apiCallTargetOptionsKey = key;
      const selected = new Set(this.selectedTargetCharts.map(String));
      this.apiCallTargetOptionsCache = [
        ...options.filter((option) => selected.has(String(option.value))),
        ...options.filter((option) => !selected.has(String(option.value))),
      ];
    }
    return this.apiCallTargetOptionsCache;
  }
  private apiCallTargetOptionsCache: LabelValueOption[] = [];
  private apiCallTargetOptionsKey = '';

  get targetCharts(): LabelValueOption[] {
    const editingComponentId = this.editingTileId
      ? this.findTileAnywhere(this.editingTileId)?.componentId
      : undefined;
    const chartTiles = this.allTilesFlat().filter((tile) => tile.componentId !== editingComponentId && tile.layoutType !== 'POPUP');
    const key = chartTiles.map((tile) => `${tile.componentId}|${tile.title}|${tile.layoutType}`).join('~');
    if (key !== this.targetChartsKey) {
      this.targetChartsKey = key;
      const seen = new Set<string>();
      const options: LabelValueOption[] = [];
      chartTiles.forEach((tile) => {
        const value = String(tile.componentId ?? '');
        if (!value || seen.has(value)) return;
        seen.add(value);
        const isInputTile = tile.layoutType === 'SLIDER' || tile.layoutType === 'INPUT_BOX' || tile.layoutType === 'DROPDOWN' || tile.layoutType === 'FILE_UPLOAD';
        options.push({ label: (tile.title || tile.layoutType) + (isInputTile ? ' (Input)' : ''), value });
      });
      this.targetChartsCache = options;
    }
    return this.targetChartsCache;
  }

  isWaterfallTarget(targetId: string): boolean {
    return this.dashboardTiles.some((tile) => tile.componentId === targetId && tile.layoutType === 'WATERFALL');
  }

  private targetStepsCache = new Map<string, { key: unknown; result: Array<{ label: string; stepType: string }> }>();

  targetSteps(targetId: string): Array<{ label: string; stepType: string }> {
    const tile = this.allTilesFlat().find((item) => item.componentId === targetId && item.layoutType === 'WATERFALL');
    const rawSteps = Array.isArray(tile?.payload?.['steps']) ? tile!.payload['steps'] as Array<Record<string, unknown>> : [];
    const cached = this.targetStepsCache.get(targetId);
    if (cached && cached.key === rawSteps) return cached.result;
    const result = rawSteps.map((step) => ({ label: String(step['label'] ?? ''), stepType: String(step['stepType'] ?? '') }));
    this.targetStepsCache.set(targetId, { key: rawSteps, result });
    return result;
  }

  trackByStepLabel(index: number, step: { label: string; stepType: string }): string {
    return step.label || `step-${index}`;
  }

  targetStepFormula(targetId: string, stepLabel: string): string {
    return this.targetStepFormulas[targetId]?.[stepLabel] || '';
  }

  setTargetStepFormula(targetId: string, stepLabel: string, value: string): void {
    const forTarget = { ...(this.targetStepFormulas[targetId] || {}) };
    forTarget[stepLabel] = value;
    this.targetStepFormulas = { ...this.targetStepFormulas, [targetId]: forTarget };
  }

  targetFormulaSummary(targetId: string): string {
    if (this.isWaterfallTarget(targetId)) {
      const perStep: Record<string, string> = this.targetStepFormulas[targetId] || {};
      return Object.keys(perStep)
        .map((label) => (perStep[label] || '').trim() ? `${label}: ${perStep[label].trim()}` : '')
        .filter(Boolean)
        .join(', ');
    }
    return (this.targetFormulas[targetId] || '').trim();
  }

  targetFormulasForPayload(
    targetId: string,
  ): Array<{ step?: string; formula: string; references?: Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> }> {
    if (this.isWaterfallTarget(targetId)) {
      return this.targetSteps(targetId)
        .map((step) => {
          const formula = this.targetStepFormula(targetId, step.label).trim();
          const references = this.referencesForFormula(targetId, formula, step.label);
          return { step: step.label, formula, ...(references ? { references } : {}) };
        })
        .filter((entry) => !!entry.formula);
    }
    const formula = (this.targetFormulas[targetId] || '').trim();
    if (!formula) return this.isInputControlTargetId(targetId) ? [{ formula: 'value' }] : [];
    const references = this.referencesForFormula(targetId, formula);
    return [{ formula, ...(references ? { references } : {}) }];
  }

  isInputControlTargetId(targetId: string): boolean {
    const layoutType = this.targetLayoutTypeForId(targetId);
    return layoutType === 'SLIDER' || layoutType === 'INPUT_BOX';
  }

  referencesForFormula(
    targetId: string,
    formula: string,
    step?: string,
  ): Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> | undefined {
    const entries = (this.targetFormulaReferences[targetId] || []).filter((entry) => (step ? entry.step === step : !entry.step));
    if (!entries.length || !formula) return undefined;
    const tokensInFormula = new Set(Array.from(formula.matchAll(/\[([^\]]+)\]/g)).map((match) => match[1].trim()));
    const references: Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> = {};
    entries.forEach((entry) => {
      const token = entry.token.trim();
      if (!token || !tokensInFormula.has(token)) return;
      references[token] = {
        inputTitle: entry.inputTitle.trim(),
        targetLabel: entry.targetLabel.trim(),
        rowLabel: entry.rowLabel.trim(),
      };
    });
    return Object.keys(references).length ? references : undefined;
  }

  rehydrateOwnFormulaReferences(componentId: string, references: unknown): void {
    const rows: TargetFormulaReferenceEntry[] = [];
    if (references && typeof references === 'object') {
      Object.keys(references as Record<string, Record<string, unknown>>).forEach((token) => {
        const ref = (references as Record<string, Record<string, unknown>>)[token] || {};
        rows.push({
          token,
          inputTitle: String(ref['inputTitle'] ?? ''),
          targetLabel: String(ref['targetLabel'] ?? ''),
          rowLabel: String(ref['rowLabel'] ?? ''),
        });
      });
    }
    this.targetFormulaReferences = { ...this.targetFormulaReferences, [componentId]: rows };
  }

  targetReferences(targetId: string, step?: string): TargetFormulaReferenceEntry[] {
    return (this.targetFormulaReferences[targetId] || []).filter((entry) => (step ? entry.step === step : !entry.step));
  }

  addTargetReference(targetId: string, step?: string): void {
    const forTarget = [...(this.targetFormulaReferences[targetId] || []), { token: '', inputTitle: '', targetLabel: '', rowLabel: '', step }];
    this.targetFormulaReferences = { ...this.targetFormulaReferences, [targetId]: forTarget };
  }

  removeTargetReference(targetId: string, entry: TargetFormulaReferenceEntry): void {
    const forTarget = (this.targetFormulaReferences[targetId] || []).filter((item) => item !== entry);
    this.targetFormulaReferences = { ...this.targetFormulaReferences, [targetId]: forTarget };
  }

  get activeLayoutSteps(): LayoutStepsDefinition | null {
    return layoutStepsFor(this.layout, this.selectedLayoutType);
  }

  layoutSectionTitle(sectionId: string, fallback: string): string {
    return layoutSectionTitle(this.layout, this.selectedLayoutType, sectionId, fallback);
  }

  layoutSectionHint(sectionId: string, fallback = ''): string {
    return layoutSectionHint(this.layout, this.selectedLayoutType, sectionId, fallback);
  }

  fieldEnabled(sectionId: string, fieldKey: string): boolean {
    return fieldEnabled(this.layout, this.selectedLayoutType, sectionId, fieldKey);
  }

  fieldLabel(sectionId: string, fieldKey: string, fallback: string): string {
    return fieldLabel(this.layout, this.selectedLayoutType, sectionId, fieldKey, fallback);
  }
  get fontWeightOptions(): LabelValueOption[] {
    return fontWeightOptionsOf(this.layout);
  }
  get measureAggregations(): LabelValueOption[] {
    return measureAggregationsOf(this.layout);
  }
  private barFormatGroupsCache: FormatControlGroup[] | null = null;
  private barFormatGroupsCacheType = '';

  get barFormatGroups(): FormatControlGroup[] {
    const cacheKey = this.isTableMatrixLayout ? `${this.selectedLayoutType}|${this.tmType}` : this.selectedLayoutType;
    if (this.barFormatGroupsCache && this.barFormatGroupsCacheType === cacheKey) {
      return this.barFormatGroupsCache;
    }
    const applicable = this.applicableFormatControls(this.selectedLayoutType)
      .filter((control) => controlVisibleForSubType(control, this.isTableMatrixLayout ? this.tmType : ''));
    this.barFormatGroupsCacheType = cacheKey;
    this.barFormatGroupsCache = groupFormatControls(applicable, this.formatGroupOrder, (name) => this.chartExposesSection(name));
    return this.barFormatGroupsCache;
  }

  isBarFormatGroupOpen(group: string): boolean {
    return this.openBarFormatGroups.has(group);
  }

  toggleBarFormatGroup(group: string): void {
    const next = new Set(this.openBarFormatGroups);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    this.openBarFormatGroups = next;
  }

  private seedBarFormatDefaults(): void {
    this.applicableFormatControls(this.selectedLayoutType).forEach((control) => {
      if (control.default !== undefined && this.barFormat[control.key] === undefined) {
        this.barFormat[control.key] = control.default;
      }
    });
  }

  get numberFormats(): LabelValueOption[] {
    return numberFormatsOf(this.layout);
  }
  get formatControls(): FormatControl[] {
    return formatControlsOf(this.layout);
  }

  applicableFormatControls(layoutType: string): FormatControl[] {
    return applicableFormatControls(this.layout, layoutType);
  }
  private readonly emptyColumnOptions: LabelValueOption[] = [];
  private formatColumnOptionsMemo = new Map<string, { key: string; result: LabelValueOption[] }>();

  formatControlOptions(control: FormatControl, tile?: DashboardTile): LabelValueOption[] {
    if (control.control === 'column-select' || control.optionsSource === 'datasetColumns') {
      const columns = tile ? this.tileFilterColumns(tile) : this.builderColumns;
      if (!columns.length) return this.emptyColumnOptions;
      const cacheKey = tile ? `tile:${tile.id}` : `builder:${this.drillDataset}`;
      const key = columns.join('|');
      const cached = this.formatColumnOptionsMemo.get(cacheKey);
      if (cached && cached.key === key) return cached.result;
      const result = columns.map((column) => ({ label: column, value: column }));
      this.formatColumnOptionsMemo.set(cacheKey, { key, result });
      return result;
    }
    const key = control.optionsKey;
    const list = key ? ((this.layout.options as Record<string, unknown> | undefined)?.[key] as LabelValueOption[] | undefined) : undefined;
    return Array.isArray(list) ? list : [];
  }

  formatControlVisible(control: FormatControl, format: Record<string, unknown> | undefined): boolean {
    return formatControlVisible(control, format);
  }

  /** Open "Request body" popup (null = closed). */
  payloadTemplateModal: PayloadTemplateModalRequest | null = null;
  private payloadTemplateTile: DashboardTile | null = null;

  hasPayloadTemplate(format: Record<string, unknown> | undefined, endpointKey: string): boolean {
    return hasPayloadTemplate(format, endpointKey);
  }

  /** "Body" button of the build form's Format accordion (targets the shared barFormat). */
  openPayloadTemplateModalForBar(control: FormatControl): void {
    this.payloadTemplateTile = null;
    this.payloadTemplateModal = {
      endpointKey: control.key,
      endpointLabel: control.label,
      template: payloadTemplatesOn(this.barFormat)[control.key],
      columns: this.builderColumns,
    };
  }

  /** "Body" button of a tile's Format popup (targets that tile's format). */
  openPayloadTemplateModalForTile(tile: DashboardTile, control: FormatControl, event?: MouseEvent): void {
    event?.stopPropagation();
    this.payloadTemplateTile = tile;
    this.payloadTemplateModal = {
      endpointKey: control.key,
      endpointLabel: control.label,
      template: payloadTemplatesOn(tile.format)[control.key],
      columns: this.tileFilterColumns(tile),
    };
  }

  closePayloadTemplateModal(): void {
    this.payloadTemplateModal = null;
    this.payloadTemplateTile = null;
  }

  /** Fold the edited template into the tile's format (or the build form's barFormat); undefined removes it. */
  onPayloadTemplateSaved(tpl: TableMatrixPayloadTemplate | undefined): void {
    const key = this.payloadTemplateModal?.endpointKey ?? '';
    if (!key) { this.closePayloadTemplateModal(); return; }
    const tile = this.payloadTemplateTile;
    const format = tile ? tile.format : this.barFormat;
    const templates = { ...payloadTemplatesOn(format) };
    if (tpl) templates[key] = tpl;
    else delete templates[key];
    const next = Object.keys(templates).length ? templates : undefined;
    if (tile) {
      tile.format = { ...(tile.format || {}), payloadTemplates: next };
      this.onTileFormatChange(tile);
      this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? tile : t));
    } else {
      this.barFormat['payloadTemplates'] = next;
      this.onBarFormatChange();
    }
    this.closePayloadTemplateModal();
  }

  selectedLayoutType = 'WATERFALL';
  selectedChartCategory = '';
  private lastSelectedLayoutType = this.selectedLayoutType;
  entityOptions: string[] = [];
  entitiesLoading = false;
  selectedDatasets: string[] = [];

  title = '';
  componentId = this.createComponentId();
  dashboardId: number | null = null;
  componentType = 'CHART';


  private apiTargetDashboards: Array<{ label: string; value: string; layoutJson: Record<string, unknown> }> = [];

  barFormat: Record<string, unknown> = {};
  openBarFormatGroups = new Set<string>();
  private entityRowsCache = new Map<string, Array<Record<string, unknown>>>();

  targetFormulas: Record<string, string> = {};
  targetStepFormulas: Record<string, Record<string, string>> = {};
  targetFormulaReferences: Record<string, TargetFormulaReferenceEntry[]> = {};
  selectedTargetCharts: string[] = [];
  buildApiCallConfig: InputApiCallConfig = defaultApiCallConfig();
  readonly apiCallModeOptions = API_CALL_MODE_OPTIONS;

  interactionsSectionOpen = false;

  get drillDataset(): string {
    return this.selectedDatasets[0] || '';
  }
  get drillDatasetColumns(): string[] {
    return this.drillDataset ? this.entityColumnsCache.get(this.drillDataset) || [] : [];
  }
  get isDrillDatasetColumnsLoading(): boolean {
    return this.isDatasetColumnsLoading(this.drillDataset);
  }

  static readonly DASH_COLS = DASH_COLS;
  static readonly DASH_ROW_PX = DASH_ROW_PX;
  static readonly DASH_GAP = DASH_GAP;

  dashboardName = '';
  dashboardTiles: DashboardTile[] = [];
  activeCrossFilter: { column: string; values: string[]; sourceTileId: string } | null = null;
  expandedListenerTileId: string | null = null;
  editingTileId: string | null = null;
  previewTileId: string | null = null;
  groupSelectMode = false;
  selectedTileIds = new Set<string>();
  activeSavedDashboardId = '';
  private restoredDashboardId = '';
  private restoredPublishedLayout: Record<string, unknown> | null = null;
  isSavingDashboard = false;
  isExistingPublishedDashboard = false;
  isDashboardFullscreen = false;
  previewEditLayout = false;
  openFormatTileId: string | null = null;
  formatPopupAlign: 'left' | 'right' = 'right';
  openInteractionTileId: string | null = null;
  private tileIdCounter = 0;
  tileDragIndex: number | null = null;
  @ViewChild('dashboardCanvas') private dashboardCanvasRef?: ElementRef<HTMLElement>;
  private activeTileInteraction:
    | {
        tile: DashboardTile;
        startX: number;
        startY: number;
        startColSpan: number;
        startRowSpan: number;
      }
    | null = null;

  isGenerating = false;
  generateError = '';
  isRecomputing = false;

  stepsPanelCollapsed = false;
  previewPanelCollapsed = false;
  dataPanelCollapsed = false;
  paneOrder: BuilderPane[] = ['preview', 'steps', 'data'];
  draggingPane: BuilderPane | null = null;

  stepsPaneWidth = 320;
  dataPaneWidth = 320;
  dataPaneFilter = '';
  fieldPaneFilter = '';
  activeExplorerEntity = '';
  explorerColumns: string[] = [];
  explorerColumnsLoading = false;

  private activePaneResize: { pane: 'steps' | 'data'; startX: number; startWidth: number; direction: 1 | -1 } | null = null;

  private entityColumnsCache = new Map<string, string[]>();
  /** Columns already fetched for a dataset (undefined until loaded) — DvmBuilderHost. */
  cachedColumns(dataset: string): string[] | undefined {
    return this.entityColumnsCache.get(dataset);
  }
  /** Rows already fetched for a dataset (undefined until loaded) — DvmBuilderHost. */
  cachedRows(dataset: string): Array<Record<string, unknown>> | undefined {
    return this.entityRowsCache.get(dataset);
  }
  private datasetColumnsLoading: Record<string, boolean> = {};
  private datasetColumnsError: Record<string, string> = {};
  private rowColumnsMemo = new Map<string, { dataset: string; source: string[] | undefined; result: string[] }>();
  /** Drop the memoised column list of a removed form row — DvmBuilderHost. */
  forgetRowColumns(rowId: string): void {
    this.rowColumnsMemo.delete(rowId);
  }

  get filteredExplorerEntities(): string[] {
    const query = this.dataPaneFilter.trim().toLowerCase();
    return query ? this.entityOptions.filter((entity) => entity.toLowerCase().includes(query)) : this.entityOptions;
  }

  get filteredExplorerColumns(): string[] {
    const query = this.fieldPaneFilter.trim().toLowerCase();
    return query ? this.explorerColumns.filter((column) => column.toLowerCase().includes(query)) : this.explorerColumns;
  }

  get paneGridTemplate(): string {
    return this.paneOrder.map((pane) => this.paneTrack(pane)).join(' 6px ');
  }

  paneGridColumn(pane: BuilderPane): number {
    return this.paneOrder.indexOf(pane) * 2 + 1;
  }

  paneCollapseIcon(pane: BuilderPane): 'chevron_left' | 'chevron_right' {
    const sitsOnRight = this.paneOrder.indexOf(pane) >= Math.ceil(this.paneOrder.length / 2);
    const collapsed = this.isPaneCollapsed(pane);
    return sitsOnRight === collapsed ? 'chevron_left' : 'chevron_right';
  }

  onPaneDragStart(pane: BuilderPane, event: DragEvent): void {
    this.draggingPane = pane;
    event.dataTransfer?.setData(PANE_DRAG_TYPE, pane);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  onPaneDragOver(event: DragEvent): void {
    if (!this.draggingPane && !Array.from(event.dataTransfer?.types || []).includes(PANE_DRAG_TYPE)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  onPaneDrop(target: BuilderPane, event: DragEvent): void {
    if (!this.draggingPane && !Array.from(event.dataTransfer?.types || []).includes(PANE_DRAG_TYPE)) return;
    event.preventDefault();
    const source = this.draggingPane || event.dataTransfer?.getData(PANE_DRAG_TYPE) as BuilderPane;
    if (!source || source === target || !this.paneOrder.includes(source)) return;
    const reordered = this.paneOrder.filter((pane) => pane !== source);
    const targetElement = event.currentTarget as HTMLElement;
    const targetBounds = targetElement.getBoundingClientRect();
    const insertAfterTarget = event.clientX > targetBounds.left + targetBounds.width / 2;
    const targetIndex = reordered.indexOf(target);
    reordered.splice(targetIndex + (insertAfterTarget ? 1 : 0), 0, source);
    this.paneOrder = reordered;
    this.draggingPane = null;
  }

  onPaneDragEnd(): void {
    this.draggingPane = null;
  }

  startDividerResize(dividerIndex: 0 | 1, event: MouseEvent): void {
    event.preventDefault();
    const leftPane = this.paneOrder[dividerIndex];
    const rightPane = this.paneOrder[dividerIndex + 1];
    const pane = rightPane === 'preview' ? leftPane : rightPane;
    if (pane === 'preview' || this.isPaneCollapsed(pane)) return;
    const startWidth = pane === 'steps' ? this.stepsPaneWidth : this.dataPaneWidth;
    this.activePaneResize = { pane, startX: event.clientX, startWidth, direction: pane === leftPane ? 1 : -1 };
  }

  isDividerDisabled(dividerIndex: 0 | 1): boolean {
    const adjacent = [this.paneOrder[dividerIndex], this.paneOrder[dividerIndex + 1]];
    return adjacent.every((pane) => pane === 'preview' || this.isPaneCollapsed(pane));
  }

  @HostListener('window:mousemove', ['$event'])
  onPaneResize(event: MouseEvent): void {
    if (this.activeTileInteraction) {
      const i = this.activeTileInteraction;
      const { colUnit, rowUnit } = this.gridUnitSizes();
      const deltaCols = colUnit ? Math.round((event.clientX - i.startX) / colUnit) : 0;
      const deltaRows = rowUnit ? Math.round((event.clientY - i.startY) / rowUnit) : 0;
      i.tile.colSpan = clampNumber(i.startColSpan + deltaCols, 2, DASH_COLS);
      i.tile.rowSpan = clampNumber(i.startRowSpan + deltaRows, 3, 40);
      return;
    }
    if (!this.activePaneResize) return;
    const delta = event.clientX - this.activePaneResize.startX;
    const { pane, startWidth, direction } = this.activePaneResize;
    if (pane === 'steps') this.stepsPaneWidth = clampNumber(startWidth + delta * direction, 240, 560);
    if (pane === 'data') this.dataPaneWidth = clampNumber(startWidth + delta * direction, 300, 620);
  }

  @HostListener('window:mouseup')
  stopPaneResize(): void {
    this.activePaneResize = null;
    if (this.activeTileInteraction) {
      this.activeTileInteraction = null;
      this.reflowCharts();
    }
  }

  private gridUnitSizes(): { colUnit: number; rowUnit: number } {
    const gap = DASH_GAP;
    const cols = DASH_COLS;
    const canvas = this.dashboardCanvasRef?.nativeElement;
    const inner = canvas ? Math.max(canvas.clientWidth, canvas.scrollWidth) - 32 /* padding */ : cols * 80;
    const track = (inner - (cols - 1) * gap) / cols;
    return { colUnit: track + gap, rowUnit: DASH_ROW_PX + gap };
  }

  reflowCharts(): void {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 60);
  }

  private paneTrack(pane: BuilderPane): string {
    if (this.isPaneCollapsed(pane)) return '48px';
    if (pane === 'steps') return `${this.stepsPaneWidth}px`;
    if (pane === 'data') return `${this.dataPaneWidth}px`;
    return 'minmax(440px, 1fr)';
  }

  private isPaneCollapsed(pane: BuilderPane): boolean {
    return pane === 'preview' ? this.previewPanelCollapsed : pane === 'steps' ? this.stepsPanelCollapsed : this.dataPanelCollapsed;
  }

  selectExplorerEntity(entity: string): void {
    this.activeExplorerEntity = entity;
    this.fieldPaneFilter = '';
    const cached = this.entityColumnsCache.get(entity);
    if (cached) {
      this.explorerColumns = cached;
      return;
    }
    this.explorerColumns = [];
    this.explorerColumnsLoading = true;
    this.datasetService.getEntityAttributes(entity, this.entityGroupName).subscribe({
      next: (attributes) => {
        const columns = this.filterVisibleColumns(attributes);
        this.entityColumnsCache.set(entity, columns);
        this.loadRowsForDataset(entity);
        if (this.activeExplorerEntity === entity) this.explorerColumns = columns;
        this.explorerColumnsLoading = false;
      },
      error: () => {
        this.explorerColumnsLoading = false;
        this.ui.showError(`Unable to load columns for "${entity}".`);
      },
    });
  }

  onExplorerFieldDragStart(event: DragEvent, dataset: string, field: string): void {
    event.stopPropagation();
    event.dataTransfer?.setData(FIELD_DRAG_TYPE, JSON.stringify({ dataset, field }));
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy';
  }

  allowFieldDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  onTargetFormulaDrop(event: DragEvent, targetId: string, stepLabel?: string): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    if (stepLabel) {
      this.setTargetStepFormula(targetId, stepLabel, this.appendFormulaToken(this.targetStepFormula(targetId, stepLabel), mapping.field));
      return;
    }
    this.targetFormulas[targetId] = this.appendFormulaToken(this.targetFormulas[targetId] || '', mapping.field);
  }

  targetLabelForId(id: string): string {
    return this.targetCharts.find((chart) => chart.value === id)?.label || id;
  }

  appendFormulaToken(current: string, field: string): string {
    const token = `[${field}]`;
    const trimmed = (current || '').trimEnd();
    return trimmed ? `${trimmed} ${token}` : token;
  }

  readFieldDrop(event: DragEvent): { dataset: string; field: string } | null {
    event.preventDefault();
    event.stopPropagation();
    const raw = event.dataTransfer?.getData(FIELD_DRAG_TYPE);
    if (!raw) return null;
    try {
      const mapping = JSON.parse(raw) as { dataset?: string; field?: string };
      return mapping.dataset && mapping.field ? { dataset: mapping.dataset, field: mapping.field } : null;
    } catch {
      return null;
    }
  }

  useDroppedSingleDataset(dataset: string): void {
    this.onSelectedDatasetsChange([dataset, ...this.selectedDatasets.filter((item) => item !== dataset)]);
    this.loadColumnsForDataset(dataset);
  }

  ngOnInit(): void {
    this.configService.loadBuilderLayout('dvm-layout', DVM_LAYOUT_BUILDER_DEFAULT_LAYOUT).subscribe((layout) => {
      const validation = this.configService.validateLayout(layout, DvmLayoutBuilderComponent.STEP_FIELD_IDS);
      if (validation.unknownFields.length) {
        console.warn('[dvm-layout-builder] config references unknown field ids; they will be skipped:', validation.unknownFields);
      }
      this.layout = layout;
      this.barFormatGroupsCache = null;
      this.selectedLayoutType = this.layoutTypes[0]?.value || 'WATERFALL';
      this.selectedChartCategory = this.categoryOfType(this.selectedLayoutType) || this.chartCategories[0]?.id || '';
      this.componentType = layout.defaults?.componentType || 'CHART';
      this.dashboardId = typeof layout.defaults?.dashboardId === 'number' ? layout.defaults.dashboardId : null;
      this.loadEntityOptions();
      this.restoreDashboardFromRoute();
      this.loadApiTargetDashboards();
    });
  }

  private async loadApiTargetDashboards(): Promise<void> {
    let email = '';
    try {
      if (await this.keycloak.isLoggedIn()) email = (await this.keycloak.loadUserProfile()).email || '';
    } catch {
    }
    if (!email) return;
    this.dashboardService.fetchOwnedDashboardsFromApi(email).subscribe({
      next: (owned) => {
        this.apiTargetDashboards = (owned || []).map((d) => ({
          label: d.name || d.slug,
          value: d.slug,
          layoutJson: (d.layoutJson || {}) as Record<string, unknown>,
        }));
        this.navDashboardsMemo = null;
        this.navComponentsMemo = null;
      },
      error: () => { /* API unavailable — fall back to localStorage list */ },
    });
  }

  private async restoreDashboardFromRoute(): Promise<void> {
    const slug = this.route.snapshot.queryParamMap.get('dashboard');
    if (!slug) return;

    const stateLayout = cloneRecord(history.state?.['layoutJson']);
    if (stateLayout && hasRenderableContent(stateLayout)) {
      this.applyRestoredDashboardLayout(stateLayout, slug);
      return;
    }

    const cached = readDynamicDashboardCache<Record<string, unknown> | null>(slug, null);
    if (cached && hasRenderableContent(cached)) {
      this.applyRestoredDashboardLayout(cached, slug);
      return;
    }

    let email = '';
    try {
      if (await this.keycloak.isLoggedIn()) email = (await this.keycloak.loadUserProfile()).email || '';
    } catch {
    }
    this.dashboardService.getPublishedLayoutBySlug(slug, email).subscribe({
      next: (layoutJson) => {
        if (!layoutJson) return;
        this.applyRestoredDashboardLayout(layoutJson, slug);
      },
      error: (error) => console.warn('[dvm-layout-builder] could not restore published dashboard:', error),
    });
  }

  private applyRestoredDashboardLayout(layoutJson: Record<string, unknown>, slug: string): void {
    this.restoredDashboardId = String(layoutJson['dashboardId'] ?? slug);
    const savedDashboards = this.persistence.consolidateSavedDashboards(
      readJson<SavedSelfServiceDashboard[]>(DvmLayoutBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY, []),
    );
    const matched = savedDashboards.find((dashboard) => dashboard.config?.['dashboardId'] === this.restoredDashboardId);
    if (matched) this.activeSavedDashboardId = matched.id;
    this.restoreTilesFromLayout(layoutJson);
  }

  private restoreTilesFromLayout(layout: Record<string, unknown>): void {
    this.dashboardName = String(layout['dashboard-name'] ?? layout['name'] ?? '') || '';
    this.restoredPublishedLayout = cloneRecord(layout['layout']);
    const rows = (layout['pageContent'] as Array<Record<string, unknown>>) || [];
    for (const row of rows) {
      const cols = (row['columns'] as Array<Record<string, unknown>>) || [];
      const found = cols
        .map((widget) => (widget['payload'] as Record<string, unknown> | undefined)?.['dashboardId'])
        .find((id) => typeof id === 'number');
      if (typeof found === 'number') {
        this.dashboardId = found;
        break;
      }
    }
    const tiles: DashboardTile[] = [];
    rows.forEach((row) => {
      const cols = (row['columns'] as Array<Record<string, unknown>>) || [];
      // Old dashboards still carry baked rows. refreshTilesOnOpen replaces them
      // a moment later, so rendering them first would show two different values.
      cols.forEach((widget) => tiles.push(this.tileFromWidget(stripWidgetData(widget))));
    });
    if (tiles.length) {
      this.dashboardTiles = applyPublishedLayoutToTiles(tiles, this.restoredPublishedLayout);
      const firstBar = tiles.find((t) => t.layoutType === 'BAR');
      const firstEditable = firstBar
        ?? tiles.find((t) => ['WATERFALL', 'DRILL_DOWN_TABLE', 'NESTED_TABLE', 'TABLE_MATRIX', 'SCATTER_BUBBLE', 'PIE_COMPOSITION', 'LINE_AREA', 'HORIZONTAL_BAR_CHART', 'KPI', 'INPUT_BOX', 'SLIDER', 'DROPDOWN', 'FILE_UPLOAD', 'POPUP'].includes(t.layoutType));
      if (firstEditable) {
        setTimeout(() => this.loadTileIntoForm(firstEditable), 0);
      }
      this.reflowCharts();
      this.refreshTilesOnOpen();
    }
  }

  /** Restored tiles carry structure only (layout_json no longer bakes rows) —
   *  replay each tile's compute payload for its data. */
  private refreshTilesOnOpen(): void {
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/components/compute');
    const params = new HttpParams().set('skipPersist', 'true');
    this.allTilesFlat().forEach((tile) => {
      if (!isChartTile(tile) || !tile.payload || !Object.keys(tile.payload).length) return;
      this.http.post<Record<string, unknown>>(url, tile.payload, { params }).subscribe({
        next: (response) => {
          const entry = response?.['result']
            ? response
            : { componentId: tile.componentId, result: { ...response, chartType: response?.['chartType'] ?? tile.layoutType, componentId: tile.componentId } };
          if (recomputeEntryHasData(entry) && this.applyResultToTile(tile, entry)) {
            this.dashboardTiles = [...this.dashboardTiles];
            this.reflowCharts();
            return;
          }
          this.seedClientTileRows(tile);
        },
        error: () => this.seedClientTileRows(tile),
      });
    });
  }

  /** Bar / line-area / scatter-bubble render client-side: when compute gives
   *  nothing usable they fall back to the dataset's raw rows. */
  private seedClientTileRows(tile: DashboardTile): void {
    const dataset = String(tile.payload?.['dataset'] ?? '').trim();
    if (!dataset) return;
    const apply = (raw: Array<Record<string, unknown>>): void => {
      // A restored tile carries its own computed columns (Formula Builder) in its capabilities.
      const caps = ((tile.layoutType === 'BAR' ? tile.barData : tile.layoutType === 'LINE_AREA' ? tile.lineAreaData : tile.layoutType === 'PIE_COMPOSITION' ? tile.pieCompositionData : tile.scatterBubbleData)?.['capabilities'] as Record<string, unknown> | undefined) ?? {};
      const computed = Array.isArray(caps['computedColumns']) ? (caps['computedColumns'] as ComputedColumn[]) : [];
      const rows = applyComputedColumns(raw, computed, Object.keys(raw[0] ?? {}), (expression, names) => this.formulaService.buildRowEvaluator(expression, names));
      const data = (tile.layoutType === 'BAR' ? tile.barData
        : tile.layoutType === 'LINE_AREA' ? tile.lineAreaData
        : tile.layoutType === 'SCATTER_BUBBLE' ? tile.scatterBubbleData
        : tile.layoutType === 'PIE_COMPOSITION' ? tile.pieCompositionData : null) as Record<string, unknown> | null;
      const fieldKey = tile.layoutType === 'SCATTER_BUBBLE' ? 'xField' : tile.layoutType === 'PIE_COMPOSITION' ? 'categoryField' : 'category';
      if (!data || !this.rowsFitClientChart(rows, data, fieldKey)) return;
      const next = { ...data, staticData: rows };
      if (tile.layoutType === 'BAR') { tile.barData = next; tile.barRows = rows; }
      else if (tile.layoutType === 'LINE_AREA') { tile.lineAreaData = next; tile.lineAreaRows = rows; }
      else if (tile.layoutType === 'PIE_COMPOSITION') { tile.pieCompositionData = next; tile.pieCompositionRows = rows; }
      else { tile.scatterBubbleData = next; tile.scatterBubbleRows = rows; }
      this.dashboardTiles = [...this.dashboardTiles];
      this.reflowCharts();
    };
    const cached = this.entityRowsCache.get(dataset);
    if (cached) { apply(cached); return; }
    this.datasetService.getEntityRows(dataset).subscribe({
      next: (rows) => { this.entityRowsCache.set(dataset, rows || []); apply(rows || []); },
      error: () => undefined,
    });
  }

  /** Published widget → builder tile (see dvm-dashboard-layout-json.utils). */
  private tileFromWidget(widget: Record<string, unknown>): DashboardTile {
    return tileFromWidget(widget, this.tileRestoreContext);
  }

  private get tileRestoreContext(): TileRestoreContext {
    return {
      restoreTileId: (value) => this.restoreTileId(value),
      createComponentId: (prefix) => this.createComponentId(prefix),
      defaultTileFormat: (layoutType) => this.defaultTileFormat(layoutType),
    };
  }

  private loadEntityOptions(): void {
    const entityGroup = this.layout.options?.entityGroup?.trim() || 'NontimeSeriesEntities';
    this.entitiesLoading = true;
    this.entitiesLoadFailed = false;
    this.datasetService.getEntities(entityGroup).subscribe({
      next: (entities) => {
        this.entityOptions = entities;
        this.entitiesLoading = false;
      },
      error: () => {
        this.entityOptions = [];
        this.entitiesLoading = false;
        this.entitiesLoadFailed = true;
        this.ui.showError('Unable to load datasets/entities.');
      },
    });
  }

  entitiesLoadFailed = false;

  reloadDatasets(): void {
    this.loadEntityOptions();
  }

  get datasetStatus(): { kind: 'error' | 'hint' | 'none'; text: string; canRetry: boolean } {
    if (this.entitiesLoading) {
      return { kind: 'hint', text: 'Loading datasets…', canRetry: false };
    }
    if (this.entitiesLoadFailed) {
      return { kind: 'error', text: 'Could not load the dataset list. Check your connection and try again.', canRetry: true };
    }
    if (!this.entityOptions.length) {
      return { kind: 'error', text: 'No datasets found. Create one in the Data Builder first, then reload.', canRetry: true };
    }
    if (!this.drillDataset) {
      return { kind: 'hint', text: 'Pick a dataset to enable the field pickers and Generate.', canRetry: false };
    }
    if (this.isDrillDatasetColumnsLoading) {
      return { kind: 'hint', text: `Loading columns from "${this.drillDataset}"…`, canRetry: false };
    }
    if (this.drillColumnsErrorMessage) {
      return { kind: 'error', text: `${this.drillColumnsErrorMessage} (see console for details)`, canRetry: false };
    }
    return { kind: 'none', text: '', canRetry: false };
  }

  onSelectedDatasetsChange(datasets: string[] | null): void {
    const previousPrimary = this.drillDataset;
    this.selectedDatasets = datasets || [];
    if (this.isDrillDownLayout || this.isNestedTableLayout || this.isTableMatrixLayout || this.isHorizontalBarLayout || this.isKpiLayout || this.isBarLayout || this.isLineAreaLayout || this.isScatterBubbleLayout || this.isDropdownLayout) {
      this.selectedDatasets.forEach((dataset) => this.loadColumnsForDataset(dataset));
    }
    if (this.drillDataset && this.drillDataset !== previousPrimary) {
      this.activeStep?.onDatasetChanged?.(this.drillDataset, previousPrimary);
    }
    this.activeStep?.onDatasetsChanged?.();
  }

  onLayoutTypeChange(): void {
    const previousLayoutType = this.lastSelectedLayoutType;
    this.lastSelectedLayoutType = this.selectedLayoutType;
    this.removePreviewTile();
    this.generateError = '';
    const owningCategory = this.categoryOfType(this.selectedLayoutType);
    if (owningCategory) this.selectedChartCategory = owningCategory;
    this.seedBarFormatDefaults();
    if (!this.editingTileId && previousLayoutType !== this.selectedLayoutType) {
      this.clearInputTargetState();
    }
    if ((this.isDrillDownLayout || this.isNestedTableLayout || this.isTableMatrixLayout || this.isHorizontalBarLayout || this.isKpiLayout || this.isBarLayout || this.isLineAreaLayout || this.isScatterBubbleLayout || this.isDropdownLayout) && this.drillDataset) {
      this.loadColumnsForDataset(this.drillDataset);
    }
    this.activeStep?.onLayoutActivated?.();
  }

  private clearInputTargetState(): void {
    this.relationHops = [];
    this.selectedTargetCharts = [];
    this.targetFormulas = {};
    this.targetStepFormulas = {};
    this.targetFormulaReferences = {};
    this.buildApiCallConfig = defaultApiCallConfig();
  }


  loadColumnsForDataset(dataset: string): void {
    if (!dataset) return;
    if (this.entityColumnsCache.has(dataset)) {
      if (!this.entityRowsCache.has(dataset)) {
        this.datasetService.getEntityRows(dataset).subscribe({
          next: (rows) => { this.entityRowsCache.set(dataset, Array.isArray(rows) ? rows : []); this.onDatasetRowsLoaded(dataset); },
          error: () => this.entityRowsCache.set(dataset, []),
        });
      }
      return;
    }

    this.datasetColumnsLoading[dataset] = true;
    delete this.datasetColumnsError[dataset];
    this.datasetService.getEntityAttributes(dataset, this.entityGroupName).subscribe({
      next: (attributes) => {
        const columns = this.filterVisibleColumns(attributes);
        this.entityColumnsCache.set(dataset, columns);
        this.activeStep?.onDatasetColumnsLoaded?.(dataset);
        this.loadRowsForDataset(dataset);
        this.datasetColumnsLoading[dataset] = false;
        if (!columns.length) {
          console.warn(`[dvm-layout-builder] "${dataset}" returned 0 attributes — raw response:`, attributes);
          this.datasetColumnsError[dataset] = `"${dataset}" returned no columns — check the entity name or its metadata.`;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.datasetColumnsLoading[dataset] = false;
        const message = error.error?.message || error.message || `Unable to load columns for "${dataset}".`;
        this.datasetColumnsError[dataset] = message;
        console.error(`[dvm-layout-builder] getEntityAttributes("${dataset}") failed:`, error);
        this.ui.showError(message);
      },
    });
  }

  private loadRowsForDataset(dataset: string): void {
    if (!dataset || this.entityRowsCache.has(dataset)) return;
    this.datasetService.getEntityRows(dataset).subscribe({
      next: (rows) => { this.entityRowsCache.set(dataset, Array.isArray(rows) ? rows : []); this.onDatasetRowsLoaded(dataset); },
      error: () => this.entityRowsCache.set(dataset, []),
    });
  }

  private onDatasetRowsLoaded(dataset: string): void {
    this.refreshPopupTilesForDataset(dataset);
    if (this.drillDataset !== dataset) return;
    this.activeStep?.onDatasetRowsLoaded?.(dataset);
  }
  isDatasetColumnsLoading(dataset: string): boolean {
    return !!dataset && !!this.datasetColumnsLoading[dataset];
  }

  columnsForRow(rowId: string, dataset: string): string[] {
    const source = dataset ? this.entityColumnsCache.get(dataset) : undefined;
    const cached = this.rowColumnsMemo.get(rowId);
    if (cached && cached.dataset === dataset && cached.source === source) {
      return cached.result;
    }
    const result = source || [];
    this.rowColumnsMemo.set(rowId, { dataset, source, result });
    return result;
  }


  onBarFormatChange(): void {
    const titleFromAccordion = this.isBarLayout || this.isLineAreaLayout || this.isWaterfallLayout || (this.isTableMatrixLayout && this.isTmDrilldown);
    if (titleFromAccordion && typeof this.barFormat['title'] === 'string') this.title = this.barFormat['title'] as string;
    const step = this.activeStep;
    if (step?.onFormatChanged) {
      step.onFormatChanged();
      return;
    }
    if (this.isKpiLayout || this.isDrillDownLayout || this.isHorizontalBarLayout) {
      this.foldFormatIntoEditingTile();
      return;
    }
  }

  tileFontFamily(format?: Record<string, unknown>): string | null {
    const family = String(format?.['fontFamily'] ?? '').trim();
    return family || null;
  }
  tileFontSizePx(format?: Record<string, unknown>): string | null {
    const size = Number(format?.['fontSize']);
    return Number.isFinite(size) && size > 0 ? `${size}px` : null;
  }

  private foldFormatIntoEditingTile(): void {
    if (!this.editingTileId) return;
    const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
    const tile = index >= 0 ? this.dashboardTiles[index] : null;
    if (!tile) return;
    tile.format = { ...(tile.format || {}), ...this.barFormat };
    this.onTileFormatChange(tile);
    this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? { ...tile } : t));
  }

  removePreviewTile(): void {
    if (!this.previewTileId) return;
    const id = this.previewTileId;
    this.previewTileId = null;
    this.dashboardTiles = this.dashboardTiles.filter((t) => t.id !== id);
  }

  drillColumnPlaceholder(kind: 'dimension' | 'measure'): string {
    if (!this.drillDataset) return 'Select a dataset first';
    if (this.isDrillDatasetColumnsLoading) return 'Loading columns...';
    if (this.drillColumnsErrorMessage) return 'Unable to load columns';
    return kind === 'dimension' ? 'Select group-by column' : 'Select value column';
  }

  get drillColumnsErrorMessage(): string {
    return this.drillDataset ? this.datasetColumnsError[this.drillDataset] || '' : '';
  }

  private get entityGroupName(): string {
    return this.layout.options?.entityGroup?.trim() || 'NontimeSeriesEntities';
  }

  private filterVisibleColumns(names: string[]): string[] {
    const hidden = new Set((this.layout.options?.hiddenColumns ?? ['uuid', '__rowKey']).map((column) => column.toLowerCase()));
    const seen = new Set<string>();
    const columns: string[] = [];
    (names || []).forEach((name) => {
      const key = String(name || '');
      if (!key || hidden.has(key.toLowerCase()) || seen.has(key)) return;
      seen.add(key);
      columns.push(key);
    });
    return columns;
  }


  get canGenerate(): boolean {
    return !this.isGenerating && !this.generateBlockers.length;
  }

  get generateBlockers(): string[] {
    if (this.isGenerating) return [];
    const missing: string[] = [];
    if (!this.title.trim()) missing.push('Title');

    const step = this.activeStep;
    if (step) {
      missing.push(...step.blockers());
      return missing;
    }

    return missing;
  }

  get generateBlockerText(): string {
    const missing = this.generateBlockers;
    return missing.length ? `Still needed: ${missing.join(', ')}` : '';
  }

  generateConfiguration(): void {
    if (!this.canGenerate) return;

    const preGenerateError = this.activeStep?.preGenerateError?.() ?? '';
    if (preGenerateError) {
      this.generateError = preGenerateError;
      return;
    }
    this.generateError = '';
    this.isGenerating = true;

    if (!this.editingTileId) {
      const previousComponentId = this.componentId;
      this.componentId = this.componentIdForLayout(this.selectedLayoutType);
      if (previousComponentId !== this.componentId && this.targetFormulaReferences[previousComponentId]?.length) {
        const { [previousComponentId]: moved, ...rest } = this.targetFormulaReferences;
        this.targetFormulaReferences = { ...rest, [this.componentId]: moved };
      }
    }

    const payload = this.buildPayload();
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');

    this.saveConfigToDb(payload);

    const step = this.activeStep;
    if (step?.generate && step.generate(payload, url)) return;

    this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: (response) => {
        this.isGenerating = false;
        const updated = this.addTileFromResponse(response, payload);
        this.ui.showSuccess(updated ? 'Chart updated on the dashboard.' : 'Chart added to the dashboard.');
      },
      error: (error: HttpErrorResponse) => {
        this.isGenerating = false;
        if (this.isHorizontalBarLayout) {
          this.http.get<Record<string, unknown>>('/assets/charts-config/horizontal-bar-chart-config.json').subscribe({
            next: (dummy) => {
              const updated = this.addTileFromResponse(dummy, payload);
              this.ui.showSuccess(updated ? 'Analytics engine not reachable - updated with sample bar-chart data.' : 'Analytics engine not reachable - added sample bar-chart data.');
            },
            error: () => {
              this.generateError = 'Unable to generate the configuration.';
              this.ui.showError(this.generateError);
            },
          });
          return;
        }
        this.generateError = error.error?.message || error.message || 'Unable to generate the configuration.';
        this.ui.showError(this.generateError);
      },
    });
  }

  private saveConfigToDb(payload: Record<string, unknown>): void {
    const saveUrl = this.layout.apiEndpoints?.saveDbConfigApi ? resolveSelfServiceApiUrl(this.layout.apiEndpoints.saveDbConfigApi) : '';
    if (!saveUrl) return;
    this.http.post<Record<string, unknown>>(saveUrl, payload).subscribe({
      error: () => this.ui.showError('Chart added, but saving its configuration to the database failed.'),
    });
  }

  /** Default Generate flow: the active family builds the tile from the response (legacy fallback renders it as a waterfall). */
  addTileFromResponse(response: Record<string, unknown> | null, payload: Record<string, unknown>): boolean {
    const base = this.newTileBase(payload);
    base.format = { ...this.defaultTileFormat(this.selectedLayoutType), ...this.barFormat };
    const step = this.activeStep;
    const tile = step?.tileFromResponse
      ? step.tileFromResponse(base, response, payload)
      : waterfallTileFromResponse(base, response, payload, this.title.trim() || undefined);
    return this.addTile(tile);
  }

  defaultTileFormat(layoutType: string): Record<string, unknown> {
    const format: Record<string, unknown> = {};
    const subType = layoutType === 'TABLE_MATRIX' ? this.tmType : '';
    this.applicableFormatControls(layoutType).forEach((control) => {
      if (subType && !controlVisibleForSubType(control, subType)) return;
      if (control.default !== undefined) format[control.key] = control.default;
    });
    return format;
  }

  /** Table & Matrix sub-type / drill-down flag — owned by the table-matrix step (DvmBuilderHost). */
  get tmType(): string {
    return this.tableMatrixStep.tmType;
  }

  get isTmDrilldown(): boolean {
    return this.tableMatrixStep.isTmDrilldown;
  }

  /** Formula Builder settings (config `options.formulaBuilder`) — DvmBuilderHost. */
  get formulaBuilderConfig(): FormulaBuilderConfig {
    return formulaBuilderConfigOf(this.layout);
  }

  /** Column pick list of the build form — DvmBuilderHost. */
  get builderColumns(): string[] {
    return this.isBarLayout ? this.barColumnStep.barColumns : this.drillDatasetColumns;
  }

  columnPlaceholder(): string {
    if (!this.drillDataset) return 'Select a dataset first';
    if (this.isDrillDatasetColumnsLoading) return 'Loading columns...';
    return 'Select column';
  }

  resolvePaletteColors(paletteKey?: string): string[] | undefined {
    return resolvePaletteColors(this.layout, paletteKey);
  }

  private refreshPopupTilesForDataset(dataset: string): void {
    const next = refreshPopupTilesForDataset(this.dashboardTiles, dataset, this.entityRowsCache.get(dataset) || []);
    if (next) this.dashboardTiles = next;
  }

  newTileBase(payload: Record<string, unknown>): DashboardTile {
    return {
      id: this.nextTileId(),
      componentId: this.componentId,
      layoutType: this.selectedLayoutType,
      title: this.title.trim() || 'Untitled',
      colSpan: 6,
      rowSpan: 8,
      payload,
    };
  }

  private allTilesFlat(): DashboardTile[] {
    return flattenTiles(this.dashboardTiles);
  }

  private findGroupMemberLocation(id: string): { group: DashboardTile; index: number } | null {
    return findGroupMemberLocation(this.dashboardTiles, id);
  }

  findTileAnywhere(id: string): DashboardTile | undefined {
    return findTileAnywhere(this.dashboardTiles, id);
  }

  updateTileInPlace(next: DashboardTile): void {
    const updated = replaceTileInPlace(this.dashboardTiles, next);
    if (updated) this.dashboardTiles = updated;
  }

  addTile(tile: DashboardTile): boolean {
    if (this.editingTileId) {
      const index = this.dashboardTiles.findIndex((item) => item.id === this.editingTileId);
      if (index >= 0) {
        const previous = this.dashboardTiles[index];
        const replacement: DashboardTile = {
          ...tile,
          id: previous.id,
          colSpan: previous.colSpan,
          rowSpan: previous.rowSpan,
          x: previous.x,
          y: previous.y,
          customSize: previous.customSize,
          format: previous.format ?? tile.format,
        };
        const next = [...this.dashboardTiles];
        next[index] = replacement;
        this.dashboardTiles = next;
        this.editingTileId = replacement.id;
        if (replacement.format) this.onTileFormatChange(replacement);
        return true;
      }
      const loc = this.findGroupMemberLocation(this.editingTileId);
      if (loc) {
        const previous = loc.group.groupMembers![loc.index];
        const replacement: DashboardTile = {
          ...tile,
          id: previous.id,
          colSpan: previous.colSpan,
          rowSpan: previous.rowSpan,
          x: previous.x,
          y: previous.y,
          customSize: previous.customSize,
          format: previous.format ?? tile.format,
        };
        const members = [...loc.group.groupMembers!];
        members[loc.index] = replacement;
        loc.group.groupMembers = members;
        this.dashboardTiles = [...this.dashboardTiles];
        this.editingTileId = replacement.id;
        if (replacement.format) this.onTileFormatChange(replacement);
        return true;
      }
      this.editingTileId = null;
    }
    this.dashboardTiles = [...this.dashboardTiles, tile];
    return false;
  }

  trackTileById(_index: number, tile: DashboardTile): string {
    return tile.id;
  }

  private nextTileId(): string {
    this.tileIdCounter += 1;
    return `tile-${this.tileIdCounter}`;
  }

  private restoreTileId(value: unknown): string {
    const id = String(value ?? '').trim();
    if (!id) return this.nextTileId();
    this.rememberTileId(id);
    return id;
  }

  private rememberTileId(id: string): void {
    const match = /^tile-(\d+)$/.exec(id);
    if (!match) return;
    this.tileIdCounter = Math.max(this.tileIdCounter, Number(match[1]) || 0);
  }

  async removeTile(tile: DashboardTile): Promise<void> {
    const result = await firstValueFrom(this.confirmationDialog.open({
      title: 'Delete chart',
      message: `Do you want to delete "${tile.title || 'this chart'}" permanently? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    }));
    const confirmed = typeof result === 'boolean' ? result : !!result?.confirmed;
    if (!confirmed) return;

    const deleteUrl = (this.layout.apiEndpoints?.deleteComponentApi || '').trim();
    if (deleteUrl && tile.componentId) {
      try {
        const response = await firstValueFrom(this.datasetService.deleteEntityRow({
          entityName: (this.layout.apiEndpoints?.deleteComponentEntityName || 'component_definitions').trim(),
          keyColumn: (this.layout.apiEndpoints?.deleteComponentKeyColumn || 'component_id').trim(),
          keyValue: tile.componentId,
        }));
        if (response?.status && response.status !== 'SUCCESS') {
          this.ui.showError(response.message || 'The server rejected the delete — the chart was not removed.');
          return;
        }
      } catch (error) {
        this.ui.showError(error instanceof Error ? error.message : 'Unable to delete the chart — please try again.');
        return;
      }
    }

    this.dashboardTiles = this.dashboardTiles.filter((item) => item.id !== tile.id);
    if (this.editingTileId === tile.id) this.editingTileId = null;
    if (deleteUrl && tile.componentId) this.ui.showSuccess('Chart deleted.');
  }

  toggleGroupSelectMode(): void {
    this.groupSelectMode = !this.groupSelectMode;
    if (!this.groupSelectMode) this.selectedTileIds = new Set();
  }

  isTileSelected(tile: DashboardTile): boolean {
    return this.selectedTileIds.has(tile.id);
  }

  toggleTileSelected(tile: DashboardTile): void {
    if (tile.layoutType === 'GROUP') return; // v1: no grouping a group
    const next = new Set(this.selectedTileIds);
    if (next.has(tile.id)) next.delete(tile.id); else next.add(tile.id);
    this.selectedTileIds = next;
  }

  get canGroupSelectedTiles(): boolean {
    return this.selectedTileIds.size >= 2;
  }

  groupSelectedTiles(): void {
    const ids = this.selectedTileIds;
    if (ids.size < 2) return;
    const selected = this.dashboardTiles.filter((t) => ids.has(t.id));
    if (selected.length < 2) return;

    const groupTile = buildGroupTile(selected, this.createComponentId('GRP'), this.defaultTileFormat('GROUP'));
    this.dashboardTiles = groupTiles(this.dashboardTiles, ids, groupTile);
    this.selectedTileIds = new Set();
    this.groupSelectMode = false;
    if (this.editingTileId && ids.has(this.editingTileId)) this.editingTileId = null;
    this.ui.showSuccess(`Grouped ${selected.length} charts into "${groupTile.title}".`);
  }

  ungroupTile(tile: DashboardTile): void {
    const next = ungroupTiles(this.dashboardTiles, tile);
    if (next) this.dashboardTiles = next;
  }

  removeGroupMember(group: DashboardTile, member: DashboardTile): void {
    if (!group.groupMembers) return;
    group.groupMembers = group.groupMembers.filter((m) => m.id !== member.id);
  }

  moveGroupMember(group: DashboardTile, member: DashboardTile, direction: -1 | 1, event?: Event): void {
    event?.stopPropagation();
    const next = moveGroupMember(group.groupMembers, member, direction);
    if (next) group.groupMembers = next;
  }

  canMoveGroupMember(group: DashboardTile, member: DashboardTile, direction: -1 | 1): boolean {
    return canMoveGroupMember(group.groupMembers, member, direction);
  }

  groupCardStyles(tile: DashboardTile): Record<string, string> {
    return tile?.layoutType === 'GROUP' ? groupCardStyleMap(tile.format) : {};
  }

  toggleGroupColumns(group: DashboardTile): void {
    group.groupColumns = (group.groupColumns || 1) >= 2 ? 1 : 2;
  }

  editTile(tile: DashboardTile, event: MouseEvent): void {
    event.stopPropagation();
    setTimeout(() => this.loadTileIntoForm(tile), 0);
  }

  loadTileIntoForm(tile: DashboardTile): void {
    this.removePreviewTile();
    const payload = tile.payload || {};
    this.editingTileId = tile.id;
    this.selectedLayoutType = tile.layoutType;
    this.componentId = tile.componentId;
    this.relationHops = this.activeRelationHops ? copyRelationHops(payload['bde_relation']) : [];
    this.title = String(payload['title'] ?? tile.title ?? '');
    this.generateError = '';
    this.stepsPanelCollapsed = false;
    this.openFormatTileId = null;

    const dataset = String(payload['dataset'] ?? '');
    const datasets = Array.isArray(payload['datasets'])
      ? (payload['datasets'] as unknown[]).map(String)
      : dataset ? [dataset] : [];
    if (!datasets.length) {
      const filters = Array.isArray(payload['filters']) ? (payload['filters'] as Array<Record<string, unknown>>) : [];
      filters.forEach((filter) => {
        const filterDataset = String(filter?.['dataset'] ?? '');
        if (filterDataset && !datasets.includes(filterDataset)) datasets.push(filterDataset);
      });
      const steps = Array.isArray(payload['steps']) ? (payload['steps'] as Array<Record<string, unknown>>) : [];
      steps.forEach((step) => {
        const stepDataset = String(step?.['dataset'] ?? '');
        if (stepDataset && !datasets.includes(stepDataset)) datasets.push(stepDataset);
      });
    }
    if (datasets.length) this.onSelectedDatasetsChange(datasets);

    const payloadTargets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
    this.selectedTargetCharts = payloadTargets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
    this.interactionsSectionOpen = this.selectedTargetCharts.length > 0;
    this.buildApiCallConfig = {
      ...defaultApiCallConfig(),
      ...(tile.dropdownControl?.apiConfig
        ?? tile.inputControl?.apiConfig
        ?? tile.fileUploadControl?.apiConfig
        ?? {}),
    };

    const step = this.stepFor(tile.layoutType);
    if (step) {
      step.loadFromTile(tile, payload);
    }

    this.onLayoutTypeChange();
  }

  startNewDashboard(): void {
    if (
      this.dashboardTiles.length &&
      !window.confirm('Start a new dashboard? This clears all charts and the dashboard name.')
    ) {
      return;
    }
    this.dashboardTiles = [];
    this.dashboardName = '';
    this.activeSavedDashboardId = '';
    this.restoredDashboardId = '';
    this.restoredPublishedLayout = null;
    this.openFormatTileId = null;
    this.previewEditLayout = false;
    this.startNewChart();
    this.ui.showSuccess('Started a new dashboard.');
  }

  openLandingPageBuilder(): void {
    void this.router.navigate(['/dvm-landing-builder']);
  }

  startNewChart(): void {
    this.removePreviewTile();
    this.editingTileId = null;
    this.title = '';
    this.componentId = this.createComponentId();
    this.interactionsSectionOpen = false;
    this.navPinnedTarget = null;
    this.navDashboardsMemo = null;
    this.barFormat = {};
    if (this.isBarLayout || this.isWaterfallLayout || this.isLineAreaLayout) this.seedBarFormatDefaults();
    this.clearInputTargetState();
    this.generateError = '';
    this.allSteps.forEach((step) => step.reset());
    this.activeStep?.onLayoutActivated?.();
  }


  onTileReorderStart(index: number, event: DragEvent): void {
    this.tileDragIndex = index;
    event.dataTransfer?.setData(TILE_DRAG_TYPE, String(index));
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  onTileReorderOver(event: DragEvent): void {
    if (this.tileDragIndex === null) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  onTileReorderDrop(targetIndex: number, event: DragEvent): void {
    event.preventDefault();
    const from = this.tileDragIndex;
    this.tileDragIndex = null;
    if (from === null || from === targetIndex) return;
    const next = [...this.dashboardTiles];
    const [moved] = next.splice(from, 1);
    next.splice(targetIndex, 0, moved);
    this.dashboardTiles = next;
    this.reflowCharts();
  }

  onTileReorderEnd(): void {
    this.tileDragIndex = null;
  }


  startTileResize(tile: DashboardTile, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.activeTileInteraction = {
      tile,
      startX: event.clientX,
      startY: event.clientY,
      startColSpan: tile.colSpan,
      startRowSpan: tile.rowSpan,
    };
  }

  toggleDashboardFullscreen(): void {
    this.isDashboardFullscreen = !this.isDashboardFullscreen;
    if (!this.isDashboardFullscreen) this.previewEditLayout = false;
    this.reflowCharts();
  }


  isPublishing = false;

  get canSaveDashboard(): boolean {
    return !this.isSavingDashboard && !this.isPublishing && this.dashboardTiles.length > 0;
  }

  get canPublish(): boolean {
    return !this.isPublishing && this.dashboardTiles.length > 0;
  }

  get publishButtonLabel(): string {
    if (this.isPublishing) return this.isExistingPublishedDashboard ? 'Updating…' : 'Publishing…';
    return this.isExistingPublishedDashboard ? 'Update' : 'Publish';
  }

  async saveDashboard(): Promise<void> {
    if (!this.canSaveDashboard) {
      this.ui.showError('Add at least one chart before saving the dashboard.');
      return;
    }

    this.isSavingDashboard = true;
    try {
      await this.saveDashboardInternal('draft');
    } finally {
      this.isSavingDashboard = false;
    }
  }

  private async saveDashboardInternal(status: 'draft' | 'published'): Promise<SavedSelfServiceDashboard | null> {
    const now = new Date().toISOString();
    const dashboardName = this.dashboardName.trim() || 'Untitled dashboard';
    const config = this.buildDashboardLayoutJson(status);
    const savedDashboards = this.persistence.consolidateSavedDashboards(
      readJson<SavedSelfServiceDashboard[]>(DvmLayoutBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY, []),
    );
    const normalizedName = normalizeDashboardName(dashboardName);
    const existing = savedDashboards.find((dashboard) =>
      dashboard.id === this.activeSavedDashboardId
      || (this.restoredDashboardId && dashboard.config?.['dashboardId'] === this.restoredDashboardId)
      || normalizeDashboardName(dashboard.name) === normalizedName,
    );

    let emailId = '';
    try {
      if (await this.keycloak.isLoggedIn()) {
        emailId = (await this.keycloak.loadUserProfile()).email || '';
      }
    } catch {
    }

    let resolvedDashboardId = this.restoredDashboardId || (existing?.config?.['dashboardId'] as string | undefined) || '';
    if (!resolvedDashboardId && emailId) {
      try {
        const owned = await firstValueFrom(this.dashboardService.fetchOwnedDashboardsFromApi(emailId));
        const match = owned.find((o) =>
          normalizeDashboardName(String(o.layoutJson?.['dashboard-name'] ?? o.layoutJson?.['name'] ?? '')) === normalizedName,
        );
        if (match) resolvedDashboardId = match.name;
      } catch {
      }
    }
    if (resolvedDashboardId) this.restoredDashboardId = resolvedDashboardId;
    this.isExistingPublishedDashboard = !!existing || !!resolvedDashboardId;

    const stableId = existing?.id || createDashboardStableId();
    const dashboardId = resolvedDashboardId || buildDashboardId(dashboardName, stableId);
    (config as Record<string, unknown>)['dashboardId'] = dashboardId;

    const savedDashboard: SavedSelfServiceDashboard = {
      id: stableId,
      name: dashboardName,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      items: [],
      layout: (config['layout'] as SavedSelfServiceDashboard['layout']) ?? { columns: DASH_COLS },
      status,
      publishedAt: status === 'published' ? now : existing?.publishedAt,
      config,
    };

    try {
      const persistedDashboard = savedDashboard;

      let publishSucceeded = false;
      try {
        const updatedExisting = await this.publishLayoutJsonToApi(config, emailId, dashboardId);
        this.isExistingPublishedDashboard = this.isExistingPublishedDashboard || updatedExisting;
        publishSucceeded = true;
      } catch (publishError) {
        console.warn('[dvm-layout-builder] saveDashboardInternal: publishApi sync failed:', publishError);
      }

      const nextDashboards = existing
        ? [
            persistedDashboard,
            ...savedDashboards.filter((dashboard) => (
              dashboard.id !== existing.id
              && normalizeDashboardName(dashboard.name) !== normalizedName
            )),
          ]
        : [persistedDashboard, ...savedDashboards];

      const cached = this.persistence.persistSavedDashboards(nextDashboards, { silent: true });
      this.activeSavedDashboardId = persistedDashboard.id;
      if (publishSucceeded) {
        this.ui.showSuccess(status === 'published' ? 'Dashboard saved and ready to publish.' : existing ? 'Dashboard updated.' : 'Dashboard saved.');
      } else {
        this.ui.showError('Saved locally, but the server could not be reached — this dashboard is not saved yet. Try again once you\'re back online.');
      }
      if (!cached) {
        console.warn('[dvm-layout-builder] Dashboard saved to the server, but the local saved-dashboards cache is full and was not updated.');
      }
      return persistedDashboard;
    } catch (error) {
      this.ui.showError(error instanceof Error ? error.message : 'Unable to save the dashboard.');
      return null;
    }
  }

  async publishDashboard(): Promise<void> {
    if (!this.canPublish) {
      this.ui.showError('Add at least one chart and name the dashboard before publishing.');
      return;
    }
    this.isPublishing = true;
    try {
      const savedDashboard = await this.saveDashboardInternal('published');
      if (!savedDashboard) return;

      const layoutJson = savedDashboard.config;
      const dashboardId = String(layoutJson['dashboardId'] ?? this.dashboardName.trim() ?? 'Untitled dashboard');
      window.dispatchEvent(new CustomEvent('dvm:dashboard-published', {
        detail: { dashboardId, name: String(layoutJson['dashboard-name'] ?? layoutJson['name'] ?? this.dashboardName.trim() ?? dashboardId) },
      }));
      this.ui.showSuccess('Dashboard published — opening it now.');
      const slug = dashboardSlug(dashboardId);
      writeDynamicDashboardCache(slug, layoutJson);
      markDashboardJustPublished(slug, layoutJson);
      this.router.navigate(['/dvm-published', slug]);
    } catch (error) {
      console.error('[dvm-layout-builder] publishDashboard failed:', error);
      const detail = extractHttpErrorDetail(error);
      this.ui.showError(detail ? `Unable to publish the dashboard: ${detail}` : 'Unable to publish the dashboard.');
    } finally {
      this.isPublishing = false;
    }
  }

  private async publishLayoutJsonToApi(layoutJson: Record<string, unknown>, emailId: string, dashboardId: string): Promise<boolean> {
    const existingRecord = emailId
      ? await firstValueFrom(this.dashboardService.findOwnedDashboardLayoutRecord(dashboardId, emailId))
      : null;
    const configuredPublishApi = (this.layout.apiEndpoints?.publishApi || '').trim();
    const publishUrl = configuredPublishApi ? resolveSelfServiceApiUrl(configuredPublishApi) : '';
    const publish$ = publishUrl
      ? this.http.post(publishUrl, buildPublishEnvelope(layoutJson, emailId, dashboardId, existingRecord?.uuid ?? null))
      : this.dashboardService.publishLayoutToApi(layoutJson, emailId, dashboardId);
    await firstValueFrom(publish$);
    this.dashboardService.invalidateOwnedRows();
    return !!existingRecord;
  }

  /** The dashboard's layout_json for save / publish (see dvm-dashboard-layout-json.utils). */
  private buildDashboardLayoutJson(status: 'draft' | 'published' = 'published'): Record<string, unknown> {
    return buildDashboardLayoutJson(
      {
        tiles: this.dashboardTiles,
        previewTileId: this.previewTileId,
        dashboardName: this.dashboardName,
        restoredPublishedLayout: this.restoredPublishedLayout,
        status,
      },
      { publishStaticData: this.publishStaticData, recomputeUrl: this.layout.apiEndpoints?.generateConfigApi?.trim() || '/components/compute' },
    );
  }

  /** `publishedView.publishStaticData` in the config — default false. */
  private get publishStaticData(): boolean {
    return this.layout.publishedView?.publishStaticData === true;
  }

  onTileSliderChange(tile: DashboardTile, range: { start: number; end: number }): void {
    this.pushInputValueAndRecompute(tile, range.end);
  }

  onTileInputValueChange(tile: DashboardTile, value: number | string): void {
    this.pushInputValueAndRecompute(tile, value);
  }

  onTileNavigate(_tile: DashboardTile, event: { targetDashboard: string; targetComponents: string[]; filterKey: string; filterValue: string }): void {
    if (!event?.targetDashboard) return;
    this.router.navigate(['/dvm-published', event.targetDashboard], {
      queryParams: {
        filterKey: event.filterKey,
        filterValue: event.filterValue,
        ...(event.targetComponents?.length ? { filterTargets: event.targetComponents.join(',') } : {}),
      },
    });
  }

  onTileTableSave(tile: DashboardTile, event: TableMatrixSaveEvent): void {
    const count = event?.rows?.length || 0;
    const drill = (tile.tableMatrixData as Record<string, unknown> | null | undefined)?.['drilldownData'] as Record<string, unknown> | undefined;
    if (drill && applyDrillDownSaveToWidgetData(drill, event)) {
      this.ui.showSuccess(`${count} change${count === 1 ? '' : 's'} saved into this tile's data — Publish carries them to the published dashboard. (Update Configuration re-fetches from the backend and overwrites them.)`);
      return;
    }
    this.ui.showSuccess(`${count} change${count === 1 ? '' : 's'} captured (builder preview only) — saving to the backend happens on the published dashboard.`);
  }

  onTileTableRefresh(tile: DashboardTile): void {
    if (!tile.tableMatrixData) return;
    const caps = (tile.tableMatrixData['capabilities'] as Record<string, unknown>) || {};
    const dataset = String(caps['dataset'] || tile.payload?.['dataset'] || '');
    if (!dataset) return;
    this.datasetService.getEntityRows(dataset).subscribe({
      next: (rows) => {
        tile.tableMatrixData = { ...(tile.tableMatrixData as Record<string, unknown>), staticData: rows };
        this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? { ...tile } : t));
      },
      error: () => { /* keep current rows on failure */ },
    });
  }

  onTileColumnReorder(tile: DashboardTile, order: string[]): void {
    if (!tile.tableMatrixData || !Array.isArray(order) || !order.length) return;
    const data = tile.tableMatrixData as Record<string, unknown>;
    const caps = { ...((data['capabilities'] as Record<string, unknown>) || {}) };
    const existing = (Array.isArray(caps['columns']) ? caps['columns'] : []) as Array<Record<string, unknown>>;
    const byField = new Map(existing.map((c) => [String(c['field']), c]));
    const reordered = order.map((field) => byField.get(field) || { field });
    caps['columns'] = reordered;
    tile.tableMatrixData = { ...data, capabilities: caps };
    const payload = tile.payload as Record<string, unknown> | undefined;
    if (payload && Array.isArray(payload['columns']) && (payload['columns'] as unknown[]).length) {
      const entries = payload['columns'] as unknown[];
      const fieldOf = (entry: unknown): string =>
        entry && typeof entry === 'object' ? String((entry as Record<string, unknown>)['field'] ?? '') : String(entry ?? '');
      const byField = new Map(entries.map((entry) => [fieldOf(entry), entry]));
      payload['columns'] = order.filter((field) => byField.has(field)).map((field) => byField.get(field));
    }
    if (this.editingTileId === tile.id) this.tableMatrixStep.syncColumnOrderFromTile(order);
    this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? { ...tile } : t));
  }

  onTileDropdownChange(tile: DashboardTile, value: string | string[], basePayload?: Record<string, unknown>): void {
    if (!tile.dropdownControl) return;
    const selected = Array.isArray(value) ? value.map(String) : value == null || value === '' ? [] : [String(value)];
    tile.dropdownControl.value = selected;
    const dropdownField = tile.dropdownControl.field ?? '';
    if (selected.length && dropdownField) {
      this.activeDropdownFilters.set(String(tile.componentId ?? ''), { field: dropdownField, values: selected });
    } else {
      this.activeDropdownFilters.delete(String(tile.componentId ?? ''));
    }
    this.applyDropdownFiltersToClientTiles();
    const clientHandledIds = new Set(
      this.dashboardTiles
        .filter((item) => this.isCrossFilterTile(item) && !!dropdownField && tileClientRows(item).some((row) => dropdownField in row))
        .map((item) => String(item.componentId ?? '')),
    );
    const configuredTargetIds = new Set(
      (tile.dropdownControl?.targets ?? []).map((id) => String(id ?? '')).filter(Boolean),
    );
    const chartTargets = this.dashboardTiles
      .filter((item) => item.componentId !== tile.componentId
        && !clientHandledIds.has(String(item.componentId ?? ''))
        && (configuredTargetIds.size
          ? configuredTargetIds.has(String(item.componentId ?? ''))
          : isChartTile(item)))
      .map((item) => ({ componentId: item.componentId, label: item.title, field: tile.dropdownControl?.field ?? '' }));
    const payload: Record<string, unknown> = {
      ...(basePayload ?? tile.payload),
      value: selected,
      targets: chartTargets,
      otherInputs: otherActiveInputValues(this.dashboardTiles, tile.componentId),
    };
    tile.payload = payload;
    const targetIds = chartTargets.map((target) => String(target.componentId ?? '')).filter(Boolean);
    const hasCustomApiCall = !!tile.dropdownControl?.apiConfig?.enabled
      && !!tile.dropdownControl.apiConfig.url?.trim();
    if (!targetIds.length && !basePayload && !hasCustomApiCall) return;
    const hasTargets = targetIds.length > 0;
    if (hasTargets) {
      this.isRecomputing = true;
      this.recomputingComponentIds = new Set(targetIds);
    }
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
    this.inputCompute.resolve(tile.dropdownControl?.apiConfig, url, payload, {
      ...payload,
      value: selected,
      componentId: tile.componentId,
      field: dropdownField,
    }).subscribe({
      next: (response) => {
        if (hasTargets) this.applyRecomputeResults(response, targetIds);
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
      },
      error: (error: HttpErrorResponse) => {
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
        if (basePayload) this.ui.showError('Dropdown added, but saving its configuration failed.');
        console.warn('[dvm-layout-builder] dropdown value push failed:', error);
      },
    });
  }

  apiCallConfigPayload(): Record<string, unknown> {
    const config = this.buildApiCallConfig;
    if (!config?.enabled && !config?.url?.trim()) return {};
    return { apiConfig: sanitizeApiCallConfig(config) };
  }

  private uploadingTileIds = new Set<string>();
  private tileUploadMessages: Record<string, string> = {};
  isTileUploading(tile: DashboardTile): boolean { return this.uploadingTileIds.has(tile.id); }
  tileUploadMessage(tile: DashboardTile): string { return this.tileUploadMessages[tile.id] || ''; }

  onTileFilePicked(tile: DashboardTile, file: File): void {
    if (!tile.fileUploadControl || !file) return;
    const targetIds = (tile.fileUploadControl.targets || []).filter(Boolean);
    this.uploadingTileIds = new Set(this.uploadingTileIds).add(tile.id);
    this.tileUploadMessages = { ...this.tileUploadMessages, [tile.id]: `Uploading ${file.name}…` };
    if (targetIds.length) this.recomputingComponentIds = new Set(targetIds);

    fileToBase64(file).then((base64) => {
      const payload: Record<string, unknown> = {
        componentId: tile.componentId,
        dashboardId: this.dashboardId,
        componentType: 'INPUT',
        inputType: 'FILE_UPLOAD',
        fileName: file.name,
        fileType: file.type,
        fileContent: base64,
        value: base64,
        defaultValue: base64,
        targets: targetIds.map((componentId) => ({ componentId, label: this.targetLabelForId(componentId) })),
      };
      const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/components/compute');
      this.inputCompute.resolve(tile.fileUploadControl?.apiConfig, url, payload, {
        value: base64,
        componentId: tile.componentId,
        fileName: file.name,
        fileType: file.type,
      }).subscribe({
        next: (response) => {
          this.uploadingTileIds.delete(tile.id);
          this.uploadingTileIds = new Set(this.uploadingTileIds);
          if (targetIds.length) this.applyRecomputeResults(response, targetIds);
          this.recomputingComponentIds = new Set();
          this.tileUploadMessages = { ...this.tileUploadMessages, [tile.id]: `Uploaded ${file.name}` };
        },
        error: () => {
          this.uploadingTileIds.delete(tile.id);
          this.uploadingTileIds = new Set(this.uploadingTileIds);
          this.recomputingComponentIds = new Set();
          this.tileUploadMessages = { ...this.tileUploadMessages, [tile.id]: `Upload failed for ${file.name}` };
        },
      });
    }).catch(() => {
      this.uploadingTileIds.delete(tile.id);
      this.uploadingTileIds = new Set(this.uploadingTileIds);
      this.recomputingComponentIds = new Set();
      this.tileUploadMessages = { ...this.tileUploadMessages, [tile.id]: `Could not read ${file.name}` };
    });
  }

  private recomputingComponentIds = new Set<string>();

  isTileRecomputing(tile: DashboardTile): boolean {
    const id = String(tile?.componentId ?? '');
    return !!id && this.recomputingComponentIds.has(id);
  }

  private pushInputValueAndRecompute(tile: DashboardTile, value: number | string): void {
    if (!tile.inputControl) return;
    tile.inputControl.value = value;
    const payload = { ...tile.payload, value, otherInputs: otherActiveInputValues(this.dashboardTiles, tile.componentId) };
    tile.payload = payload;
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
    const payloadTargets = (payload as Record<string, unknown>)['targets'];
    const targetIds = (Array.isArray(payloadTargets) ? (payloadTargets as Array<Record<string, unknown>>) : [])
      .map((t) => String(t?.['componentId'] ?? ''))
      .filter(Boolean);

    const sideEffectTiles = sideEffectInputsFor(tile, this.dashboardTiles);
    this.isRecomputing = true;
    this.recomputingComponentIds = new Set([...targetIds, ...sideEffectTiles.map((t) => t.componentId)]);

    const runMainCompute = () => this.inputCompute.resolve(tile.inputControl?.apiConfig, url, payload, {
      value,
      componentId: tile.componentId,
    }).subscribe({
      next: (response) => {
        this.applyRecomputeResults(response, targetIds);
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
      },
      error: (error: HttpErrorResponse) => {
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
        console.warn('[dvm-layout-builder] input value push failed:', error);
      },
    });

    if (!sideEffectTiles.length) {
      runMainCompute();
      return;
    }

    const sideEffectRequests = sideEffectTiles.map((sideTile) => {
      const sidePayload = { ...sideTile.payload, otherInputs: otherActiveInputValues(this.dashboardTiles, sideTile.componentId) };
      return this.http.post<Record<string, unknown>>(url, sidePayload).pipe(
        map((response) => ({ sideTile, response, error: null as unknown })),
        catchError((error) => of({ sideTile, response: null, error })),
      );
    });

    forkJoin(sideEffectRequests).subscribe((results) => {
      results.forEach(({ sideTile, response, error }) => {
        if (response) {
          const sideTargets = (Array.isArray((sideTile.payload as Record<string, unknown>)?.['targets'])
            ? ((sideTile.payload as Record<string, unknown>)['targets'] as Array<Record<string, unknown>>)
            : []).map((t) => String(t?.['componentId'] ?? '')).filter(Boolean);
          this.applyRecomputeResults(response, sideTargets);
        } else if (error) {
          console.warn('[dvm-layout-builder] SSA $ side-effect recompute for', sideTile.componentId, 'failed:', error);
        }
      });
      runMainCompute();
    });
  }

  private applyRecomputeResults(response: Record<string, unknown> | null, targetIds: string[] = []): void {
    const results = (response?.['results'] ?? []) as Array<Record<string, unknown>>;
    if (!Array.isArray(results) || !results.length) return;
    const allowedIds = new Set(targetIds);
    let changed = false;
    results.forEach((entry) => {
      if (!recomputeEntryHasData(entry)) return;
      const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
      const entryComponentId = String(entry?.['componentId'] ?? result?.['componentId'] ?? '').trim();
      const chartType = resolveRecomputeChartType(entry);
      const tile = findRecomputeTargetTile(this.allTilesFlat(), entryComponentId, chartType, allowedIds);
      if (tile && this.applyResultToTile(tile, entry)) changed = true;
    });
    if (changed) {
      this.dashboardTiles = [...this.dashboardTiles];
      this.reflowCharts();
    }
  }

  /**
   * Hand a recompute result to the family that owns it. An INPUT-typed result
   * feeds the input controls themselves (dropdown options / slider value);
   * otherwise the result's chartType (or the tile's layout) picks the family.
   */
  private applyResultToTile(tile: DashboardTile, entry: Record<string, unknown>): boolean {
    const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
    const inputTarget = resolveRecomputeChartType(entry) === 'INPUT'
      && (tile.layoutType === 'DROPDOWN' || tile.layoutType === 'SLIDER' || tile.layoutType === 'INPUT_BOX');
    const family = inputTarget ? tile.layoutType : String(result?.['chartType'] ?? tile.layoutType ?? '');
    const format = (tile.format as Record<string, unknown>) || this.defaultTileFormat(tile.layoutType);
    return this.stepFor(family)?.applyRecomputeResult?.(tile, { entry, result, format }) ?? false;
  }

  rowsFitClientChart(
    rows: Array<Record<string, unknown>>,
    data: Record<string, unknown>,
    fieldKey: 'category' | 'xField' | 'categoryField',
  ): boolean {
    if (!Array.isArray(rows) || !rows.length) return true;
    const field = String((data['capabilities'] as Record<string, unknown> | undefined)?.[fieldKey] ?? '');
    return !field || rows.some((row) => field in row);
  }

  invalidatePreview(): void {
    this.tableMatrixStep.reshapeEditingDrillTile();
  }

  chartTargetsPayload(): Record<string, unknown> {
    if (!this.selectedTargetCharts.length) return {};
    return {
      targets: this.selectedTargetCharts.map((componentId) => ({
        componentId,
        label: this.targetLabelForId(componentId),
      })),
    };
  }
  headerFormatPayload(): Record<string, unknown> {
    const source = this.isScatterBubbleLayout ? this.scatterBubbleStep.sbFormat : this.isPieCompositionLayout ? this.pieCompositionStep.pcFormat : this.barFormat;
    if (!source || !Object.keys(source).length) return {};
    return { format: { ...source } };
  }

  buildPayload(): Record<string, unknown> {
    const payload = this.buildComponentPayload();
    if (this.activeRelationHops) {
      payload['bde_relation'] = copyRelationHops(this.relationHops);
    }
    return payload;
  }

  private buildComponentPayload(): Record<string, unknown> {
    const step = this.activeStep;
    if (step) return step.buildPayload();

    return {
      componentId: this.componentId,
      dashboardId: this.dashboardId,
      componentType: this.componentType,
      chartType: this.selectedLayoutType,
      title: this.title.trim(),
      datasets: [...this.selectedDatasets],
      ...this.chartTargetsPayload(),
      ...this.headerFormatPayload(),
    };
  }

  isFormattableTile(tile: DashboardTile): boolean {
    return isFormattableTile(tile);
  }

  tileTakesHeaderFormat(tile: DashboardTile): boolean {
    return tileTakesHeaderFormat(tile);
  }

  formatControlsFor(tile: DashboardTile): FormatControl[] {
    const subType = this.tileTableSubType(tile);
    return this.applicableFormatControls(tile.layoutType)
      .filter((control) => controlVisibleForSubType(control, subType));
  }

  private tileTableSubType(tile: DashboardTile): string {
    if (tile.layoutType !== 'TABLE_MATRIX') return '';
    const caps = ((tile.tableMatrixData as Record<string, unknown> | undefined)?.['capabilities'] as Record<string, unknown>) || {};
    return String(caps['tableType'] ?? '');
  }

  get formatGroupOrder(): string[] {
    return formatGroupOrderOf(this.layout);
  }

  private openFormatGroups = new Map<string, Set<string>>();

  private tileFormatGroupsCache = new Map<string, { key: string; groups: FormatControlGroup[] }>();
  formatGroupsFor(tile: DashboardTile): FormatControlGroup[] {
    const key = `${tile.layoutType}::${this.formatControls.length}::${this.tileTableSubType(tile)}`;
    const cached = this.tileFormatGroupsCache.get(tile.id);
    if (cached && cached.key === key) return cached.groups;
    const groups = this.buildFormatGroupsFor(tile);
    this.tileFormatGroupsCache.set(tile.id, { key, groups });
    return groups;
  }

  private buildFormatGroupsFor(tile: DashboardTile): FormatControlGroup[] {
    return groupFormatControls(this.formatControlsFor(tile), this.formatGroupOrder);
  }

  isFormatGroupOpen(tile: DashboardTile, group: string, index: number): boolean {
    const set = this.openFormatGroups.get(tile.id);
    if (!set) return index === 0;
    return set.has(group);
  }

  toggleFormatGroup(tile: DashboardTile, group: string, index: number): void {
    let set = this.openFormatGroups.get(tile.id);
    if (!set) {
      set = new Set<string>();
      const groups = this.formatGroupsFor(tile);
      if (groups[0]) set.add(groups[0].name);
      this.openFormatGroups.set(tile.id, set);
    }
    if (set.has(group)) set.delete(group);
    else set.add(group);
  }

  toggleTileFormat(tile: DashboardTile, event?: MouseEvent): void {
    if (this.openFormatTileId === tile.id) {
      this.openFormatTileId = null;
      return;
    }
    if (!tile.format) tile.format = this.defaultTileFormat(tile.layoutType);
    this.formatPopupAlign = this.resolveFormatPopupAlign(event);
    this.openFormatTileId = tile.id;
  }

  /** Tile whose Interactions overlay is open (null = closed). */
  interactionTile: DashboardTile | null = null;

  canEditInteractions(tile: DashboardTile): boolean {
    return !!tile && tile.layoutType !== 'GROUP';
  }

  toggleTileInteractions(tile: DashboardTile, event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.openInteractionTileId === tile.id) {
      this.closeTileInteractions();
      return;
    }
    this.openFormatTileId = null;
    this.formatPopupAlign = this.resolveFormatPopupAlign(event);
    this.interactionTile = tile;
    this.openInteractionTileId = tile.id;
  }

  closeTileInteractions(): void {
    this.openInteractionTileId = null;
    this.interactionTile = null;
  }

  /** The Interactions overlay saved: store the targets on the tile and, for an input, push its value to them. */
  onInteractionsSaved({ tile, isInput, targets }: TileInteractionsSave): void {
    const payload = { ...tile.payload, targets };
    tile.payload = payload;
    const targetIds = targets.map((t) => t.componentId);
    if (tile.inputControl) tile.inputControl.targets = [...targetIds];
    if (tile.dropdownControl) tile.dropdownControl.targets = [...targetIds];
    this.updateTileInPlace({ ...tile });

    const recomputeIds = isInput ? targetIds.filter(Boolean) : [];
    if (recomputeIds.length) {
      const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
      this.isRecomputing = true;
      this.recomputingComponentIds = new Set(recomputeIds);
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: (response) => { this.applyRecomputeResults(response, recomputeIds); this.isRecomputing = false; this.recomputingComponentIds = new Set(); },
        error: () => { this.isRecomputing = false; this.recomputingComponentIds = new Set(); },
      });
    }
    this.ui.showSuccess('Interactions updated.');
    this.closeTileInteractions();
  }

  private resolveFormatPopupAlign(event?: MouseEvent): 'left' | 'right' {
    const trigger = event?.currentTarget as HTMLElement | null;
    const canvas = this.dashboardCanvasRef?.nativeElement;
    if (!trigger || !canvas) return 'right';
    const triggerRect = trigger.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();
    const popupWidth = 280;
    const gutter = 12;
    return triggerRect.right - popupWidth < canvasRect.left + gutter ? 'left' : 'right';
  }

  /** A tile's Format popup changed: sync the title, let the tile's family re-derive its widget data, re-render. */
  onTileFormatChange(tile: DashboardTile): void {
    const format = tile.format || {};
    if (format['title']) tile.title = format['title'] as string;
    this.stepFor(tile.layoutType)?.applyFormatToTile?.(tile, format);
    this.updateTileInPlace({ ...tile });
    this.reflowCharts();
  }

  onTileCrossFilter(sourceTile: DashboardTile, event: { column: string; value: string | null; values?: string[] }): void {
    const column = this.effectiveTileFilterKey(sourceTile) || event.column;
    const values = Array.isArray(event.values)
      ? event.values
      : (event.value ? [event.value] : []);
    if (!values.length) {
      this.activeCrossFilter = null;
    } else {
      this.activeCrossFilter = { column, values, sourceTileId: sourceTile.id };
    }
    this.postCrossFilterEvent(sourceTile, column, values);
    this.applyCrossFilterToTiles();
  }

  private postCrossFilterEvent(sourceTile: DashboardTile, column: string, values: string[]): void {
    if (!values.length) return; // filter cleared — nothing to report
    const dataset = tileDatasets(sourceTile)[0] ?? '';
    if (!dataset) return; // no dataset to scope the call to
    const url = buildSelfServiceApiUrl(`/${encodeURIComponent(dataset)}/dynamic_filter`);
    const body = {
      uuid: null,
      crossFilter: {
        column,
        values,
        sourceTileId: sourceTile.id,
        listenerTileIds: this.effectiveListenerTileIds(sourceTile),
      },
      filters: [{ column, operator: 'IN', values }],
    };
    this.http.post(url, body).pipe(catchError(() => of(null))).subscribe();
  }

  clearCrossFilter(): void {
    if (!this.activeCrossFilter) return;
    this.activeCrossFilter = null;
    this.applyCrossFilterToTiles();
  }


  isCrossFilterTile(tile: DashboardTile): boolean {
    return isCrossFilterTile(tile);
  }

  canConfigureCrossFilter(tile: DashboardTile): boolean {
    return canConfigureCrossFilter(tile);
  }

  private applyCrossFilterToTiles(): void {
    const filter = this.activeCrossFilter;
    const sourceTile = filter ? this.dashboardTiles.find((t) => t.id === filter.sourceTileId) : undefined;
    const listeners = sourceTile ? new Set(this.effectiveListenerTileIds(sourceTile)) : new Set<string>();
    this.dashboardTiles = applyCrossFilterToClientTiles(this.dashboardTiles, filter, listeners);
    this.dashboardTiles.forEach((tile) => {
      if (!isRecomputeCrossFilterTile(tile)) return;
      if (filter && tile.id === filter.sourceTileId) return;
      if (filter && listeners.has(tile.id)) this.recomputeWaterfallTile(tile, filter);
      else this.restoreWaterfallTile(tile);
    });
    this.reflowCharts();
  }


  private activeDropdownFilters = new Map<string, { field: string; values: string[] }>();

  private applyDropdownFiltersToClientTiles(): void {
    const liveIds = new Set(this.dashboardTiles.map((t) => String(t.componentId ?? '')));
    [...this.activeDropdownFilters.keys()].forEach((key) => { if (!liveIds.has(key)) this.activeDropdownFilters.delete(key); });
    const filters = [...this.activeDropdownFilters.values()].filter((f) => f.field && f.values.length);
    this.dashboardTiles = applyDropdownFiltersToClientTiles(this.dashboardTiles, filters);
    this.reflowCharts();
  }

  private waterfallFilterSeq = new Map<string, number>();

  private recomputeWaterfallTile(tile: DashboardTile, filter: { column: string; values: string[] }): void {
    const seq = (this.waterfallFilterSeq.get(tile.id) ?? 0) + 1;
    this.waterfallFilterSeq.set(tile.id, seq);
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
    const payload = {
      ...tile.payload,
      filters: [{ column: filter.column, operator: 'IN', values: filter.values }],
    };
    this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: (response) => {
        if (this.waterfallFilterSeq.get(tile.id) !== seq) return; // stale response
        const rawBars = (Array.isArray(response) ? response : (response as { data?: WaterfallBar[] })?.data || []) as WaterfallBar[];
        if (!rawBars.length) return; // nothing usable — keep the current view
        const bars = applyStepSignsToWaterfallBars(rawBars, tile.payload);
        const current = this.dashboardTiles.find((t) => t.id === tile.id);
        if (!current) return;
        const updated: DashboardTile = {
          ...current,
          waterfallBarsAll: current.waterfallBarsAll ?? current.waterfallBars,
          waterfallBars: bars,
        };
        updated.previewOption = buildWaterfallPreviewOption(bars, updated.format);
        updated.waterfallData = buildWaterfallTileData(updated);
        this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? updated : t));
        this.reflowCharts();
      },
      error: () => undefined,
    });
  }

  private restoreWaterfallTile(tile: DashboardTile): void {
    this.waterfallFilterSeq.set(tile.id, (this.waterfallFilterSeq.get(tile.id) ?? 0) + 1);
    if (!tile.waterfallBarsAll) return; // never showed filtered bars
    const restored: DashboardTile = { ...tile, waterfallBars: tile.waterfallBarsAll, waterfallBarsAll: undefined };
    restored.previewOption = buildWaterfallPreviewOption(restored.waterfallBars!, restored.format);
    restored.waterfallData = buildWaterfallTileData(restored);
    this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? restored : t));
  }


  reactableTilesFor(sourceTile: DashboardTile): DashboardTile[] {
    return reactableTilesFor(sourceTile, this.dashboardTiles, this.previewTileId);
  }

  private effectiveListenerTileIds(sourceTile: DashboardTile): string[] {
    return effectiveListenerTileIds(sourceTile, this.dashboardTiles, this.previewTileId);
  }

  effectiveTileFilterKey(tile: DashboardTile): string {
    return effectiveTileFilterKey(tile);
  }

  private tileFilterColumnsMemo = new Map<
    string,
    { key: string; sources: (string[] | undefined)[]; result: string[] }
  >();
  tileFilterColumns(tile: DashboardTile): string[] {
    const datasets = tileDatasets(tile);
    const key = datasets.join('|');
    const sources = datasets.map((dataset) => this.entityColumnsCache.get(dataset));
    const cached = this.tileFilterColumnsMemo.get(tile.id);
    if (cached && cached.key === key && cached.sources.every((source, i) => source === sources[i])) {
      return cached.result;
    }
    const seen = new Set<string>();
    const columns: string[] = [];
    sources.forEach((source) => {
      (source || []).forEach((column) => {
        if (!seen.has(column)) {
          seen.add(column);
          columns.push(column);
        }
      });
    });
    this.tileFilterColumnsMemo.set(tile.id, { key, sources, result: columns });
    return columns;
  }

  toggleListenerEditor(tile: DashboardTile, event?: Event): void {
    event?.stopPropagation();
    this.expandedListenerTileId = this.expandedListenerTileId === tile.id ? null : tile.id;
    if (this.expandedListenerTileId === tile.id) {
      tileDatasets(tile).forEach((dataset) => this.loadColumnsForDataset(dataset));
    }
  }

  setTileFilterKey(tile: DashboardTile, value: string): void {
    const trimmed = (value || '').trim();
    this.dashboardTiles = this.dashboardTiles.map((t) =>
      t.id === tile.id ? { ...t, filterKey: trimmed || undefined } : t,
    );
    if (this.activeCrossFilter?.sourceTileId === tile.id) this.clearCrossFilter();
  }

  isTileListener(sourceTile: DashboardTile, candidateTileId: string): boolean {
    return this.effectiveListenerTileIds(sourceTile).includes(candidateTileId);
  }

  toggleTileListener(sourceTile: DashboardTile, candidateTileId: string): void {
    const current = this.effectiveListenerTileIds(sourceTile);
    const next = current.includes(candidateTileId)
      ? current.filter((id) => id !== candidateTileId)
      : [...current, candidateTileId];
    this.dashboardTiles = this.dashboardTiles.map((t) =>
      t.id === sourceTile.id ? { ...t, listenerTileIds: next } : t,
    );
    if (this.activeCrossFilter?.sourceTileId === sourceTile.id) this.applyCrossFilterToTiles();
  }

  resetTileListeners(sourceTile: DashboardTile): void {
    this.dashboardTiles = this.dashboardTiles.map((t) =>
      t.id === sourceTile.id ? { ...t, filterKey: undefined, listenerTileIds: undefined } : t,
    );
    if (this.activeCrossFilter?.sourceTileId === sourceTile.id) this.clearCrossFilter();
  }


  private componentIdCounter = 0;

  private createComponentId(prefix = 'CMP'): string {
    this.componentIdCounter += 1;
    return `${prefix}-${this.componentIdCounter}-${Math.floor(100 + Math.random() * 900)}`;
  }

  private componentIdForLayout(layoutType: string): string {
    return this.createComponentId(componentIdPrefixFor(layoutType));
  }
}
